import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
    Box as MuiBox,
    Snackbar as MuiSnackbar,
    Alert as MuiAlert,
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    DialogActions as MuiDialogActions,
    Button as MuiButton,
    Typography as MuiTypography,
    useMediaQuery,
    useTheme,
    Backdrop as MuiBackdrop,
    CircularProgress as MuiCircularProgress
} from "@mui/material";
import { Warning as WarningIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { useAuth } from "@clerk/clerk-react";

// Import MUI Theme
import muiTheme from "./theme/muiTheme";

// Import Toaster for notifications
import { Toaster, toast } from "sonner";

// Component imports
import { Navigation } from "./components/Navigation";
import { shouldShowContextMeshReturn } from "./components/meshStrength/contextMeshNavigation";
import { BackgroundActivityProvider, useBackgroundActivity } from "./components/backgroundActivity";
import { BatchGenerationProvider } from "./components/batchGeneration";
import { UploadCenter } from "./components/UploadCenter";
import Profile from "./components/Profile";
import Integrations from "./components/integrations";
import Admin from "./components/Admin";
import AIChatPanel, { type ChatActionsHandle } from "./components/AIChatPanel";
import { createUIEventBus, type NavigationAdapter, type FileIntakeAdapter } from "@codenexus/react";
import { deriveHostLocationContext, isProductScopedChatView, resolveActiveProductContext, resolveScopedWorkProductElement } from "./components/AIChatPanelHost";
import { createFileIntakeGate } from "./components/fileIntakeGate";
import type { CurrentHierarchyContext } from "./components/chatTools/createChatTools";
import type { AppChatEvents, WorkProductConfirmationCandidate } from "./components/chatTools/types";
import InitiativeCreationModal from "./components/InitiativeCreationModal";
import ProjectsHub from "./components/ProjectsHub";
import { ProductsDashboard } from "./components/products";
import { NewProductScreen, type NewProductFormHandle } from "./components/newProduct/NewProductScreen";
import { completeProductCreationNavigation } from "./components/newProduct/productCreationNavigation";
import type { ProductCreationOperation } from "./components/newProduct/productCreationOperationsApi";
import { shouldUseDurableProductCreation } from "./components/newProduct/productCreationRouting";
import { MeshStrengthView } from "./components/meshStrength/MeshStrengthView";
import { ContextMeshConsole } from "./components/contextMesh/ContextMeshConsole";
import { StandaloneContextMeshCreationScreen } from "./components/contextMesh/StandaloneContextMeshCreationScreen";
import { StandaloneContextMeshRefreshScreen } from "./components/contextMesh/StandaloneContextMeshRefreshScreen";
import { getContextMesh, listContextMeshProductLinks } from "./components/contextMesh/contextMeshApi";
import { useAccessManifest } from "@/auth/AccessManifestProvider";
import {
    CONTEXT_MESH_CAPABILITIES,
    canOpenStandaloneContextMesh,
    canAccessAppView,
    defaultAuthorizedView,
    hasManifestCapability,
    resolveAuthorizedView
} from "@/auth/accessNavigation";
import type { FeatureStudioEntityOpenResult } from "./components/meshStrength/contextMeshBridgeAdapter";
import type { NewProductDraftPatch, NewProductDraftSnapshot } from "./components/newProduct/state";
import { useFeatureFlag } from "@/features";
import { UpdateProductModal } from "../../components/Project/UpdateProduct";
import AddNewUserStoryModal from "./components/AddNewUserStoryModal";
import WorkProductEditor from "./components/WorkProductEditor";
import { LinearProgressIndicator } from "./components/ProgressIndicator";

// Import extracted components and views
import FeatureStudioView, { type OptionalWorkProductScope } from "./components/FeatureStudioView";
import {
    findClosestWorkProductGenerationSource,
    findWorkProductGenerationSource
} from "./components/feature-studio/sddWorkflow";

// Import AlertDialog components for delete confirmation
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "./components/ui/alert-dialog";

// Import types and constants
import {
    WorkProduct,
    Feature,
    Initiative,
    ChatMessage,
    JiraSyncFeedback,
    InitiativeData,
    FeatureFormData,
    WorkProductProgress,
    GlobalProgress
} from "./types/index";
import { STRATEGIC_THEMES, WORK_PRODUCT_LIBRARY } from "./constants/index";
import { generateId, getPhaseColor, initializeWorkProductsByPhase, getDefaultMatrix } from "./utils/helpers";
import { deriveActiveWorkProductForChat, type ChatWorkProductScope } from "./utils/chatScope";
import { resolveSelectedWorkProductOnViewChange } from "./utils/workProductViewRestore";
import { parseEditorDeepLink } from "./utils/editorDeepLink";
import {
    NEW_PRODUCT_ROUTE,
    PRODUCTS_ROUTE,
    featureStudioUrl,
    managementRouteForView,
    normalizePathWithSearch,
    productIdFromSearch,
    routeForView,
    viewForPath
} from "./utils/appViewRoutes";
import { resolveManifestNavigation } from "./utils/manifestNavigation";
import {
    editorHistoryState,
    resolveNavigationProjectId,
    shouldUseBrowserBackForEditor,
    workProductNavigationUrls
} from "./utils/workProductNavigation";
import {
    CONTEXT_MESH_HUB_ROUTE,
    NEW_CONTEXT_MESH_ROUTE,
    contextMeshDraftUrl,
    contextMeshHubUrl,
    contextMeshRefreshUrl,
    parseContextMeshHubDeepLink
} from "./utils/contextMeshDeepLink";
import {
    featureStudioDeepLinkKey,
    parseFeatureStudioDeepLink,
    parseSameOriginFeatureStudioHref,
    type FeatureStudioDeepLink
} from "./utils/featureStudioDeepLink";
import {
    parseProductContextMeshDeepLink
} from "./utils/productContextMeshDeepLink";

// Import validation utilities
import { validateProject, validateFeature, PLATFORM_TERMINOLOGY } from "./utils/terminologyValidation";

const buildDocLatestVersionContextKey = (
    workProductId: string,
    projectId?: number,
    featureId?: string | number,
    userStoryId?: string
): string => `doc-latest:${projectId || 0}:${featureId || ""}:${userStoryId || ""}:${workProductId}`;

// Import custom hooks
import { useLocalStorageState, useExpandedAccordions } from "./hooks/useLocalStorage";
import { useDataGovernance } from "./hooks/useDataGovernance";
import { useBrowserLocationSnapshot } from "./hooks/useBrowserLocationSnapshot";

// Import handler creators
import { createWorkProductHandlers } from "./handlers/workProductHandlers";
import { createFeatureHandlers } from "./handlers/featureHandlers";
import { createProjectHandlers } from "./handlers/projectHandlers";
import { createChatHandlers } from "./handlers/chatHandlers";

// Import constants and utilities
import { getFeatureMockData, getDefaultExpandedAccordions } from "./utils/appConstants";

// Import Redux hooks and actions
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchProjects, fetchProjectRebuildHierarchy, setCurrentProject } from "@/state/project/projectSlice";
import { fetchWorkProductsByPhase } from "@/state/workproduct/workProductSlice";
import { fetchUserProfileThunk } from "@/state/userProfile/userProfileSlice";
import { fetchFeatureUserStories, deleteUserStory, removeFeatureUserStories } from "@/state/userStories/userStoriesSlice";
import { clearEditorSurface, selectEditorSurface } from "@/state/activeEditor/activeEditorSlice";

// Import API functions
import { fetchProjectFeaturesApi, deleteFeatureApi, setCurrentProjectApi } from "@/api/api";
import type { Feature as ApiFeature } from "@/api/models/feature";
import { getFeatureStudioUserStory } from "@/api/userStoryApi";
import fetchWithAuth from "@/api/secureFetch";
import { getJobStatusManager, type JobStreamUpdate } from "@/api/workProcessor";

// Define position interface for AI Chat
interface AIChatPosition {
    position: "floating" | "docked" | "minimized";
    x: number;
    y: number;
    width: number;
    height: number;
}

interface PendingWorkProductGenerationRequest {
    workProductId?: string;
    workProductName?: string;
    targetFeatureName?: string;
    targetUserStoryName?: string;
    targetUserStoryNumber?: string;
    confirmationToken?: string;
    phase: string;
    forceNewVersion?: boolean;
    scope?: {
        level: "PRODUCT" | "FEATURE" | "USERSTORY";
        featureId: number | null;
        userStoryId: string | null;
    };
}

interface ResolverScope {
    level: "PRODUCT" | "FEATURE" | "USERSTORY";
    featureId?: number;
    userStoryId?: string;
}

interface ResolvedWorkProductScope {
    level: "PRODUCT" | "FEATURE" | "USERSTORY";
    featureId: number | null;
    userStoryId: string | null;
}

interface ResolverConfirmationCandidate {
    rank?: number;
    confidence?: number;
    workProduct?: {
        id?: string;
        name?: string;
    };
    scope?: {
        level?: "PRODUCT" | "FEATURE" | "USERSTORY";
        featureId?: number | null;
        userStoryId?: string | null;
    };
    confirmationToken?: string;
}

function normalizeWorkProductLookup(value: unknown): string {
    return String(value ?? "").trim().toLowerCase();
}

function isLikelyCanonicalStoryId(value: unknown): boolean {
    const storyId = String(value ?? "").trim();
    if (!storyId) return false;
    if (/^[0-9]+$/.test(storyId)) return false;
    return true;
}

function toInitiativeFeature(apiFeature: ApiFeature): Feature {
    return {
        id: apiFeature.featureid?.toString() || generateId(),
        name: apiFeature.title || "Untitled Feature",
        description: apiFeature.description || "",
        goals: apiFeature.goals || [],
        successMetrics: apiFeature.success_metrics || [],
        labels: [],
        status:
            apiFeature.status === "in_progress"
                ? "in-progress"
                : apiFeature.status === "proposed"
                  ? "draft"
                  : apiFeature.status === "completed"
                    ? "completed"
                    : "draft",
        priority: "medium",
        assignees: [],
        workProducts: [],
        workProductsByPhase: new Map(),
        uploads: [],
        detailedRequirements: [],
        children: [],
        createdAt: apiFeature.created_at ? new Date(apiFeature.created_at) : new Date(),
        lastModified: apiFeature.updated_at ? new Date(apiFeature.updated_at) : new Date(),
        projectid: apiFeature.projectid
    } as Feature;
}

type WorkProductResolverResponse =
    | {
          requestId?: string;
          status: "resolved_and_started";
          jobId?: string;
          message?: string;
          resolved?: {
              workProductId?: string;
              workProductName?: string;
              scope?: {
                  level?: "PRODUCT" | "FEATURE" | "USERSTORY";
                  featureId?: number | null;
                  userStoryId?: string | null;
              };
          };
      }
    | {
          requestId?: string;
          status: "confirmation_required";
          message?: string;
          confirmationToken?: string;
          candidates?: ResolverConfirmationCandidate[];
          onConfirmTemplate?: {
              tool?: "generate_work_product";
              arguments?: {
                  forceNewVersion?: boolean;
              };
          };
          closestMatch?: {
              workProductId?: string;
              workProductName?: string;
          };
          onConfirm?: {
              tool?: "generate_work_product";
              arguments?: AppChatEvents["generate_work_product"];
          };
      }
    | {
          requestId?: string;
          status: "not_available";
          message?: string;
          availableWorkProducts?: string[];
      };

const WORK_PRODUCT_RESOLVER_CONTRACT_VERSION = "1";

const AI_CHAT_DEFAULT_WIDTH = 400;
const AI_CHAT_MIN_WIDTH = 280;
const AI_CHAT_MAX_RATIO = 0.6;
const NAV_RAIL_WIDTH = 56;
const CHAT_SCOPE_STORAGE_KEY = "intelligenic.chatScope.v1";

interface ChatProductScope {
    projectId: number | null;
    projectName: string | null;
}

interface PersistedChatScope {
    view: string;
    product: ChatProductScope | null;
    workProduct: ChatWorkProductScope | null;
}

const VALID_VIEWS = new Set(["projects", "context-mesh-console", "new-context-mesh", "refresh-context-mesh", "features", "editor", "uploads", "profile", "integrations", "admin", "new-product", "refresh-product", "mesh-strength"]);

function readPersistedChatScope(): PersistedChatScope | null {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.sessionStorage.getItem(CHAT_SCOPE_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<PersistedChatScope>;
        if (!parsed || typeof parsed !== "object") return null;
        const view = typeof parsed.view === "string" && VALID_VIEWS.has(parsed.view) ? parsed.view : "projects";
        return {
            view,
            product: parsed.product ?? null,
            workProduct: parsed.workProduct ?? null
        };
    } catch {
        return null;
    }
}

function persistChatScope(scope: PersistedChatScope): void {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(CHAT_SCOPE_STORAGE_KEY, JSON.stringify(scope));
    } catch {
        // Best effort only: failing to persist chat scope should never block UI.
    }
}

function AppContent() {
    const { manifest } = useAccessManifest();
    const browserLocation = useBrowserLocationSnapshot();
    const hasProductStudio = manifest?.entitlements.product_studio ?? false;
    const hasContextMesh = manifest?.entitlements.context_mesh ?? false;
    const canViewContextMeshConsole = hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewConsole);
    const canCreateStandaloneContextMesh = hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.createMesh);
    const canViewContextMeshAnalysis = hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.viewAnalysis);
    const canManageContextMeshFiles = hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.manageFiles);
    const canManageContextMeshIntegrations = hasManifestCapability(manifest, CONTEXT_MESH_CAPABILITIES.manageIntegrations);
    const { userId } = useAuth();
    const initialPersistedChatScope = readPersistedChatScope();
    const initialEditorDeepLink = useMemo(
        () => (
            typeof window === "undefined" || !canAccessAppView(manifest, "editor")
                ? null
                : parseEditorDeepLink(window.location.pathname, window.location.search)
        ),
        [manifest]
    );
    const [editorDeepLink, setEditorDeepLink] = useState(initialEditorDeepLink);
    const initialFeatureStudioDeepLink = useMemo(
        () => (
            typeof window === "undefined" || !canAccessAppView(manifest, "features")
                ? null
                : parseFeatureStudioDeepLink(window.location.pathname, window.location.search)
        ),
        [manifest]
    );
    const [featureStudioDeepLink, setFeatureStudioDeepLink] = useState<FeatureStudioDeepLink | null>(initialFeatureStudioDeepLink);
    useEffect(() => {
        if (manifest && !canAccessAppView(manifest, "features")) {
            setFeatureStudioDeepLink(null);
        }
        if (manifest && !canAccessAppView(manifest, "editor")) {
            setEditorDeepLink(null);
        }
    }, [manifest]);
    const [suppressFeatureStudioWelcome, setSuppressFeatureStudioWelcome] = useState(false);
    const locationHydratedRef = useRef(false);
    const deepLinkHydrationStartedRef = useRef(false);
    const featureStudioHydrationKeyRef = useRef<string | null>(null);
    const pendingFeatureStudioOpenRef = useRef<{
        key: string;
        resolve: (result: FeatureStudioEntityOpenResult) => void;
        timeoutId: ReturnType<typeof setTimeout>;
    } | null>(null);
    const { openForJob: openBackgroundActivityForJob } = useBackgroundActivity();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const dispatch = useAppDispatch();
    const dbProjects = useAppSelector(state => state.projects.projects);
    const projectsStatus = useAppSelector(state => state.projects.status);
    const token = useAppSelector(state => state.clientToken.token);
    const userProfile = useAppSelector(state => state.userProfiles?.userProfile);
    const currentProjectFromStore = useAppSelector(state => state.projects.currentProject);
    const editorSurface = useAppSelector(selectEditorSurface);

    const settleFeatureStudioOpen = useCallback((key: string, result: FeatureStudioEntityOpenResult) => {
        const pending = pendingFeatureStudioOpenRef.current;
        if (!pending || pending.key !== key) return;
        clearTimeout(pending.timeoutId);
        pendingFeatureStudioOpenRef.current = null;
        pending.resolve(result);
    }, []);

    useEffect(() => () => {
        const pending = pendingFeatureStudioOpenRef.current;
        if (!pending) return;
        clearTimeout(pending.timeoutId);
        pendingFeatureStudioOpenRef.current = null;
        pending.resolve({ opened: false, reason: "Feature Studio navigation was interrupted." });
    }, []);

    const { isEnabled: productsDashboardV2Enabled } = useFeatureFlag("products-dashboard-v2");
    const { isEnabled: asyncProductCreationEnabled } = useFeatureFlag("async-product-creation");
    const durableProductCreationEnabled = shouldUseDurableProductCreation({
        manifest,
        asyncProductCreationEnabled,
        productsDashboardV2Enabled,
        hasAuthenticatedUser: Boolean(userId)
    });

    // Core application state with improved typing
    // Always start on the work-products/projects page after sign-in.
    // Persisted chat scope is still used for conversational context fallback,
    // but should not restore UI navigation state.
    const [currentView, setCurrentView] = useState<string>(() => {
        if (!manifest) return "projects";
        const requestedView = typeof window === "undefined"
            ? defaultAuthorizedView(manifest)
            : viewForPath(window.location.pathname, window.location.search) ?? defaultAuthorizedView(manifest);
        return resolveAuthorizedView(manifest, requestedView);
    });
    const routeDefaultProductId = useMemo(() => {
        if (
            typeof window === "undefined" ||
            (currentView !== "uploads" && currentView !== "integrations")
        ) {
            return undefined;
        }
        return productIdFromSearch(browserLocation.search);
    }, [browserLocation.search, currentView]);
    useEffect(() => {
        if (!manifest || typeof window === "undefined") return;
        const hydrateFromLocation = !locationHydratedRef.current;
        const legacyProductContextLink = parseProductContextMeshDeepLink(window.location.pathname);
        const requestedView = legacyProductContextLink
            ? "projects"
            : viewForPath(window.location.pathname, window.location.search);
        const decision = resolveManifestNavigation({
            manifest,
            currentView,
            requestedView,
            hydrateFromLocation
        });
        locationHydratedRef.current = true;

        if (hydrateFromLocation) {
            const nextEditorDeepLink = decision.view === "editor"
                ? parseEditorDeepLink(window.location.pathname, window.location.search)
                : null;
            const nextFeatureStudioDeepLink = decision.view === "features"
                ? parseFeatureStudioDeepLink(window.location.pathname, window.location.search)
                : null;
            deepLinkHydrationStartedRef.current = false;
            featureStudioHydrationKeyRef.current = null;
            setEditorDeepLink(nextEditorDeepLink);
            setFeatureStudioDeepLink(nextFeatureStudioDeepLink);
        } else if (decision.view !== currentView) {
            if (decision.view !== "editor") setEditorDeepLink(null);
            if (decision.view !== "features") setFeatureStudioDeepLink(null);
        }

        if (decision.view !== currentView) setCurrentView(decision.view);
        if (decision.replaceRoute || legacyProductContextLink) {
            window.history.replaceState(
                { view: decision.view },
                "",
                routeForView(decision.view) ?? "/dashboard"
            );
        }
    }, [manifest, currentView]);

    const [showContextMeshReturn, setShowContextMeshReturn] = useState(false);
    const [standaloneContextMeshId, setStandaloneContextMeshId] = useState<string | null>(() => {
        if (typeof window === "undefined") return null;
        const link = parseContextMeshHubDeepLink(window.location.pathname);
        return link?.action === "console" || link?.action === "refresh" ? link.contextMeshId : null;
    });
    const [standaloneContextMeshTitle, setStandaloneContextMeshTitle] = useState<string | null>(null);
    const [standaloneContextMeshAccess, setStandaloneContextMeshAccess] = useState<{
        contextMeshId: string; accessRole: "owner" | "editor" | "viewer";
    } | null>(null);
    const [contextMeshRefreshReturnTarget, setContextMeshRefreshReturnTarget] = useState<"hub" | "mesh">("mesh");
    const [standaloneContextMeshDraftId, setStandaloneContextMeshDraftId] = useState<number | null>(() => {
        if (typeof window === "undefined") return null;
        const link = parseContextMeshHubDeepLink(window.location.pathname);
        return link?.action === "draft" ? link.draftId : null;
    });
    const [standaloneContextMeshSection, setStandaloneContextMeshSection] = useState<"console" | "files" | "integrations">(() => {
        if (typeof window === "undefined") return "console";
        return parseContextMeshHubDeepLink(window.location.pathname)?.section ?? "console";
    });
    const [sharedContextMeshTargetId, setSharedContextMeshTargetId] = useState<string | null>(null);
    useEffect(() => {
        setStandaloneContextMeshAccess(null);
        if (!token || !standaloneContextMeshId) {
            setStandaloneContextMeshTitle(null);
            return;
        }
        let cancelled = false;
        void getContextMesh(token, standaloneContextMeshId)
            .then(contextMesh => {
                if (!cancelled) {
                    setStandaloneContextMeshTitle(contextMesh.name.trim() || null);
                    setStandaloneContextMeshAccess({ contextMeshId: standaloneContextMeshId, accessRole: contextMesh.accessRole });
                }
            })
            .catch(() => {
                if (!cancelled) setStandaloneContextMeshTitle(null);
            });
        return () => { cancelled = true; };
    }, [standaloneContextMeshId, token]);
    useEffect(() => {
        if (!token || !standaloneContextMeshId || currentView === "refresh-context-mesh") return;
        let cancelled = false;
        void listContextMeshProductLinks(token, standaloneContextMeshId)
            .then(links => {
                if (cancelled || links.length === 0) return;
                const preferred = links.find(link => link.linkRole === "primary") ?? links[0];
                setStandaloneContextMeshId(null);
                setStandaloneContextMeshSection("console");
                if (hasProductStudio) {
                    setCurrentView("projects");
                    if (typeof window !== "undefined") {
                        window.history.replaceState(
                            { view: "projects", productId: preferred.productId },
                            "",
                            PRODUCTS_ROUTE
                        );
                    }
                } else {
                    setCurrentView("context-mesh-console");
                    if (typeof window !== "undefined") {
                        window.history.replaceState({ view: "context-mesh-console" }, "", CONTEXT_MESH_HUB_ROUTE);
                    }
                }
            })
            .catch(() => {
                // The Console owns the recoverable error state when scope resolution fails.
            });
        return () => { cancelled = true; };
    }, [currentView, hasProductStudio, standaloneContextMeshId, token]);
    useEffect(() => {
        if (!standaloneContextMeshId) return;
        const sectionIsAuthorized = standaloneContextMeshSection === "console" ||
            (standaloneContextMeshSection === "files" && canManageContextMeshFiles) ||
            (standaloneContextMeshSection === "integrations" && canManageContextMeshIntegrations);
        if (sectionIsAuthorized) return;
        setStandaloneContextMeshSection("console");
        if (typeof window !== "undefined") {
            window.history.replaceState(
                { view: "context-mesh-console", contextMeshId: standaloneContextMeshId },
                "",
                contextMeshHubUrl(standaloneContextMeshId)
            );
        }
    }, [canManageContextMeshFiles, canManageContextMeshIntegrations, standaloneContextMeshId, standaloneContextMeshSection]);
    const [newlyCreatedContextMeshId, setNewlyCreatedContextMeshId] = useState<string | null>(null);
    const [contextMeshHubRefreshSignal, setContextMeshHubRefreshSignal] = useState(0);
    const [productContextMeshAnalysisRefreshSignal, setProductContextMeshAnalysisRefreshSignal] = useState(0);
    const [workspaceContextMeshAnalysisRefreshSignal, setWorkspaceContextMeshAnalysisRefreshSignal] = useState(0);
    const [productsRefreshSignal, setProductsRefreshSignal] = useState<number>(0);
    const [productCreationOperationSeed, setProductCreationOperationSeed] = useState<{
        userId: string;
        operation: ProductCreationOperation;
    } | null>(null);
    const [integrationsRefreshSignal, setIntegrationsRefreshSignal] = useState<number>(0);
    const [uploadsRefreshSignal, setUploadsRefreshSignal] = useState<number>(0);
    const [selectedWorkProduct, setSelectedWorkProduct] = useState<WorkProduct | null>(null);
    // Stash for the work product nulled out on editor->features (see
    // resolveSelectedWorkProductOnViewChange). Lets a later chip-driven
    // `onViewChange("editor")` from AIChatPanel (C1 fix) restore the same
    // work product without the user re-selecting it from Feature Studio.
    // Cleared on hard resets (project switch, back-to-products) so a stale
    // work product from an unrelated context is never resurrected.
    const lastKnownWorkProductRef = useRef<WorkProduct | null>(null);
    const persistedChatScopeRef = useRef<PersistedChatScope | null>(initialPersistedChatScope);
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
    const [selectedUserStory, setSelectedUserStory] = useState<any | null>(null);
    const [selectedProject, setSelectedProject] = useState<Initiative | null>(null);
    const [optionalWorkProductScope, setOptionalWorkProductScope] = useState<OptionalWorkProductScope>({
        projectId: null,
        feature: null,
        story: null
    });

    const optionalScopeProjectId = Number((selectedProject as any)?.project_id ?? selectedProject?.id ?? 0) || null;
    useEffect(() => {
        setOptionalWorkProductScope(current =>
            current.projectId === optionalScopeProjectId
                ? current
                : { projectId: optionalScopeProjectId, feature: null, story: null }
        );
    }, [optionalScopeProjectId]);

    // Store navigation context to preserve hierarchy selection when navigating back from editor
    const [navigationContext, setNavigationContext] = useState<{
        feature: Feature | null;
        userStory: any | null;
        expandedHierarchy: string[];
    }>({ feature: null, userStory: null, expandedHierarchy: [] });

    // Trigger to force work product refresh when navigating back from editor
    const [workProductRefreshTrigger, setWorkProductRefreshTrigger] = useState<number>(0);
    const [workProductRefreshScope, setWorkProductRefreshScope] = useState<ResolvedWorkProductScope | null>(null);

    // Work product state
    const [lastUsedMatrixId, setLastUsedMatrixId] = useState<string | null>(null);
    const [expandedWorkProducts, setExpandedWorkProducts] = useState<Set<string>>(new Set());
    const [generatingWorkProducts, setGeneratingWorkProducts] = useState<Set<string>>(new Set());
    const [processingWorkProducts, setProcessingWorkProducts] = useState<Set<string>>(new Set());
    const [expandedInitiatives, setExpandedInitiatives] = useState<Set<string>>(new Set(["init-1"]));

    // Hierarchy expanded state - to preserve tree expansion when navigating back from editor
    const [hierarchyExpandedItems, setHierarchyExpandedItems] = useState<string[]>(["project-0"]);

    // Use work products from Redux store
    const workProductsByPhaseFromStore = useAppSelector(state => state.workProductByPhase?.workProductsByPhase || {});
    const [workProductsByPhase, setWorkProductsByPhase] = useState<Record<string, WorkProduct[]>>(() =>
        Object.keys(workProductsByPhaseFromStore).length > 0 ? workProductsByPhaseFromStore : initializeWorkProductsByPhase()
    );

    // Progress state management with proper typing
    const [workProductProgress, setWorkProductProgress] = useState<Map<string, WorkProductProgress>>(new Map());
    const [globalProgress, setGlobalProgress] = useState<GlobalProgress | null>(null);
    const [featureCreationProgress, setFeatureCreationProgress] = useState<GlobalProgress | null>(null);

    // UI state with localStorage persistence
    const [isHierarchyCollapsed, setIsHierarchyCollapsed] = useState<boolean>(true);
    const [featureStudioTab, setFeatureStudioTab] = useState<"sdd">("sdd");
    const [expandedAccordions, setExpandedAccordions] = useExpandedAccordions(getDefaultExpandedAccordions());

    // Chat and AI state
    const [showAIModal, setShowAIModal] = useState<boolean>(false);
    const [chatStep, setChatStep] = useState<number>(0);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [currentChatInput, setCurrentChatInput] = useState<string>("");
    const [showThemeHelp, setShowThemeHelp] = useState<boolean>(false);
    const [initiativeData, setInitiativeData] = useState<InitiativeData>({
        theme: "",
        goal: "",
        userRoles: [],
        files: []
    });

    // Chat panel is always rendered; only its width is user-tunable.
    const [chatWidth, setChatWidth] = useLocalStorageState<number>("aiChatWidth", AI_CHAT_DEFAULT_WIDTH);

    const handleChatWidthChange = useCallback(
        (next: number) => {
            const max = Math.floor(window.innerWidth * AI_CHAT_MAX_RATIO);
            setChatWidth(Math.max(AI_CHAT_MIN_WIDTH, Math.min(max, next)));
        },
        [setChatWidth]
    );

    // The chat used to be toggleable; now it's permanent. Several downstream
    // components still receive `onAIChatToggle`, so hand them a stable no-op
    // so their existing prop signatures don't need to change.
    const noopChatToggle = useCallback(() => {}, []);

    // Modal states
    const [jiraSyncFeedback, setJiraSyncFeedback] = useState<JiraSyncFeedback>({
        show: false,
        message: "",
        severity: "success"
    });
    const [showAddFeatureModal, setShowAddFeatureModal] = useState<boolean>(false);
    const [showAddUserStoryModal, setShowAddUserStoryModal] = useState<boolean>(false);
    const [featuresRefreshTrigger, setFeaturesRefreshTrigger] = useState<number>(0);
    const [deletingFeatureId, setDeletingFeatureId] = useState<string | null>(null);
    const [featureToDelete, setFeatureToDelete] = useState<any | null>(null);
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState<boolean>(false);
    const [userStoryToDelete, setUserStoryToDelete] = useState<any | null>(null);
    const [showDeleteUserStoryDialog, setShowDeleteUserStoryDialog] = useState<boolean>(false);

    // Feature editing state
    const [isFeatureInfoEditing, setIsFeatureInfoEditing] = useState<boolean>(false);
    const [featureInfoData, setFeatureInfoData] = useState<FeatureFormData>({
        title: "",
        description: "",
        goals: [],
        successMetrics: [],
        labels: []
    });
    const [showEditWarning, setShowEditWarning] = useState<boolean>(false);
    const [showWorkProductsImpactModal, setShowWorkProductsImpactModal] = useState<boolean>(false);
    const [impactedWorkProducts, setImpactedWorkProducts] = useState<string[]>([]);

    // Validation state
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

    // Loading state
    const [isLoadingNewInitiative, setIsLoadingNewInitiative] = useState<boolean>(false);
    const [loadingProgress, setLoadingProgress] = useState<number>(0);

    // File upload refs
    const fileInputRef = useRef<HTMLInputElement>(null);
    const chatScrollRef = useRef<HTMLDivElement>(null);
    // Imperative handle into CodeNexus chat — populated by AIChatPanel once
    // the chat session is live. Exposes chat actions (e.g. fireSkill) for
    // programmatic use from outside the chat tree. The Products Studio CTAs
    // no longer use this to start the build-new-product skill; they now
    // navigate directly to the new-product view.
    const chatActionsRef = useRef<ChatActionsHandle | null>(null);
    const newProductDraftRef = useRef<NewProductDraftSnapshot | null>(null);
    const standaloneContextMeshDirtyRef = useRef(false);
    const newProductFormRef = useRef<NewProductFormHandle | null>(null);
    // Which saved product-creation draft the new-product screen should resume;
    // null means start a fresh draft.
    const [newProductDraftId, setNewProductDraftId] = useState<number | null>(null);
    // FDE pre-answer for the New-vs-Existing gate, carried by the
    // navigate_to_new_product event; null for manual/user navigation.
    const [newProductInitialKind, setNewProductInitialKind] = useState<"new" | "existing" | null>(null);
    const [newProductInitialPatch, setNewProductInitialPatch] = useState<NewProductDraftPatch | null>(null);
    const [requestedBuildProductId, setRequestedBuildProductId] = useState<string | null>(null);
    // Which product the "refresh-product" view should load (KAN-1464); null means
    // no refresh in progress, so the view mount is a no-op until it's set.
    const [refreshProductId, setRefreshProductId] = useState<number | null>(null);

    // Main application data - Start with empty array, will be populated from database
    const [initiatives, setInitiatives] = useState<Initiative[]>([]);

    // Load real projects from database when token is available
    useEffect(() => {
        if (token) {
            dispatch(fetchProjects());
            dispatch(fetchUserProfileThunk());
        }
    }, [token, dispatch]);

    // Load work products when current project changes
    useEffect(() => {
        if (token && currentProjectFromStore?.project_id) {
            dispatch(
                fetchWorkProductsByPhase({
                    projectId: currentProjectFromStore.project_id,
                    level: "PRODUCT"
                })
            );
        }
    }, [token, currentProjectFromStore?.project_id, dispatch]);

    // Sync work products from Redux store to local state
    // Always sync to ensure fresh data, especially when navigating back to product level
    useEffect(() => {
        setWorkProductsByPhase(workProductsByPhaseFromStore);
    }, [workProductsByPhaseFromStore]);

    // Keep the application shell and Product Hub lightweight. Feature Studio
    // owns feature hydration for the Product the user actually opens; eagerly
    // fetching every Product's features here creates an N-request startup
    // waterfall and blocks unrelated navigation on large accounts.
    useEffect(() => {
        if (!dbProjects?.length) {
            setInitiatives([]);
            return;
        }
        setInitiatives(current => {
            const currentById = new Map(current.map(project => [project.id, project]));
            return dbProjects.map(project => {
                const id = project.project_id?.toString() || "unknown";
                const previous = currentById.get(id);
                return {
                    id,
                    name: project.project_name || "Unnamed Project",
                    code: `PRJ-${project.project_id || 0}`,
                    description: project.project_description || "",
                    dueDate: project.last_updated || "",
                    startDate: project.created_date || "",
                    teamSize: 0,
                    progress: previous?.progress ?? 0,
                    status: project.status === "Active" ? "active" : "inactive",
                    cardState: project.card_state ?? "standard",
                    build: project.build ?? null,
                    theme: "Innovation",
                    uploads: previous?.uploads ?? [],
                    features: previous?.features ?? [],
                    project_id: project.project_id,
                    external_link_type: project.external_link_type,
                    external_link_key: project.external_link_key
                } as unknown as Initiative;
            });
        });
    }, [dbProjects]);

    // Rehydrate a browser deep link into the existing state-driven editor.
    // This is intentionally one-shot: after the target is resolved, normal
    // in-app navigation owns the state again.
    useEffect(() => {
        if (!editorDeepLink || !token || deepLinkHydrationStartedRef.current) return;
        if (projectsStatus === "loading" || projectsStatus === "idle") return;
        if (dbProjects.length > 0 && initiatives.length === 0) return;

        deepLinkHydrationStartedRef.current = true;
        const fail = (message: string) => {
            toast.error(message);
            setSelectedWorkProduct(null);
            setSelectedFeature(null);
            setSelectedUserStory(null);
            setSelectedProject(null);
            setShowContextMeshReturn(false);
            setCurrentView("projects");
            if (typeof window !== "undefined") {
                window.history.replaceState({ view: "projects" }, "", "/dashboard");
            }
        };

        const hydrate = async () => {
            let project = initiatives.find(item => Number((item as any).project_id ?? item.id) === editorDeepLink.projectId);
            if (!project) {
                fail("That Product is unavailable or you no longer have access to it.");
                return;
            }

            if (editorDeepLink.featureId !== undefined) {
                const features = (await fetchProjectFeaturesApi(editorDeepLink.projectId, token)).map(toInitiativeFeature);
                project = { ...project, features };
                const hydratedProject = project;
                setInitiatives(current => current.map(item => item.id === hydratedProject.id ? hydratedProject : item));
            }

            let feature: Feature | null = null;
            if (editorDeepLink.featureId !== undefined) {
                feature = project.features.find(item => Number((item as any).featureid ?? item.id) === editorDeepLink.featureId) ?? null;
                if (!feature) {
                    fail("That Feature is no longer available in this Product.");
                    return;
                }
            }

            let story: any | null = null;
            if (editorDeepLink.storyId) {
                if (!feature || editorDeepLink.featureId === undefined) {
                    fail("The User Story deep link is missing its Feature context.");
                    return;
                }
                const result = await dispatch(fetchFeatureUserStories({
                    featureId: editorDeepLink.featureId,
                    idToken: token
                })).unwrap();
                story = result.userStories.find(item => String(item.id) === editorDeepLink.storyId) ?? null;
                if (!story) {
                    fail("That User Story is no longer available in this Product.");
                    return;
                }
            }

            const phases = await dispatch(fetchWorkProductsByPhase({
                projectId: editorDeepLink.projectId,
                level: editorDeepLink.level,
                featureId: editorDeepLink.featureId,
                userStoryId: editorDeepLink.storyId
            })).unwrap();
            const workProduct = Object.values(phases)
                .flat()
                .find(item => Number(item.id) === editorDeepLink.workProductId) ?? null;
            if (!workProduct) {
                fail("That Work Product is unavailable in the requested Product context.");
                return;
            }
            setSelectedProject(project);
            setSelectedFeature(feature);
            setSelectedUserStory(story);
            setSelectedWorkProduct(workProduct);
            lastKnownWorkProductRef.current = workProduct;
            setNavigationContext({
                feature,
                userStory: story,
                expandedHierarchy: [
                    `P:${editorDeepLink.projectId}`,
                    ...(editorDeepLink.featureId !== undefined ? [`F:${editorDeepLink.featureId}`] : [])
                ]
            });
            setShowContextMeshReturn(false);
            setCurrentView("editor");
        };

        void hydrate().catch(error => {
            console.error("Failed to hydrate Work Product editor deep link:", error);
            fail("Unable to load that Work Product. Please try again.");
        });

    }, [dbProjects.length, dispatch, editorDeepLink, initiatives, projectsStatus, token]);

    // Rehydrate analyzer recommendation links into the state-driven Feature
    // Studio hierarchy. User-story links intentionally resolve the story by
    // id first because the analyzer contract does not include its feature id.
    useEffect(() => {
        if (!featureStudioDeepLink || !token) return;
        if (projectsStatus === "loading" || projectsStatus === "idle") return;
        if (dbProjects.length > 0 && initiatives.length === 0) return;

        const hydrationKey = featureStudioDeepLinkKey(featureStudioDeepLink);
        if (featureStudioHydrationKeyRef.current === hydrationKey) return;
        featureStudioHydrationKeyRef.current = hydrationKey;

        const fail = (message: string) => {
            toast.error(message);
            setSelectedWorkProduct(null);
            setSelectedFeature(null);
            setSelectedUserStory(null);
            setSelectedProject(null);
            setShowContextMeshReturn(false);
            setFeatureStudioDeepLink(null);
            setCurrentView("projects");
            if (typeof window !== "undefined") {
                window.history.replaceState({ view: "projects" }, "", PRODUCTS_ROUTE);
            }
            settleFeatureStudioOpen(hydrationKey, { opened: false, reason: message });
        };

        const hydrate = async () => {
            let project = initiatives.find(
                item => Number((item as any).project_id ?? item.id) === featureStudioDeepLink.projectId
            );
            if (!project) {
                fail("That Product is unavailable or you no longer have access to it.");
                return;
            }


            if (featureStudioDeepLink.level !== "PRODUCT") {
                const features = (await fetchProjectFeaturesApi(featureStudioDeepLink.projectId, token)).map(toInitiativeFeature);
                project = { ...project, features };
                const hydratedProject = project;
                setInitiatives(current => current.map(item => item.id === hydratedProject.id ? hydratedProject : item));
            }

            try {
                await setCurrentProjectApi(featureStudioDeepLink.projectId, token);
            } catch (error) {
                console.warn("Failed to set the Feature Studio deep-link Product as active:", error);
            }

            let feature: Feature | null = null;
            let story: any | null = null;

            if (featureStudioDeepLink.level === "FEATURE") {
                feature = project.features.find(
                    item => String((item as any).featureid ?? item.id) === featureStudioDeepLink.nodeId
                ) ?? null;
                if (!feature) {
                    fail("That Feature is no longer available in this Product.");
                    return;
                }
            }

            if (featureStudioDeepLink.level === "USERSTORY") {
                try {
                    story = await getFeatureStudioUserStory(featureStudioDeepLink.nodeId!, token);
                } catch (error) {
                    console.error("Failed to resolve the Feature Studio user-story deep link:", error);
                    fail("That User Story is no longer available in this Product.");
                    return;
                }
                if (Number(story.project_id) !== featureStudioDeepLink.projectId) {
                    fail("That User Story is not available in the requested Product.");
                    return;
                }
                if (story.feature_id == null) {
                    fail("That User Story is not linked to an owning Feature yet.");
                    return;
                }
                feature = project.features.find(
                    item => String((item as any).featureid ?? item.id) === String(story.feature_id)
                ) ?? null;
                if (!feature) {
                    fail("The Feature containing that User Story is no longer available.");
                    return;
                }
            }

            const expandedHierarchy = [
                `P:${featureStudioDeepLink.projectId}`,
                ...(feature ? [`F:${String((feature as any).featureid ?? feature.id)}`] : [])
            ];
            setSelectedProject(project);
            setSelectedFeature(feature);
            setSelectedUserStory(story);
            setSelectedWorkProduct(null);
            lastKnownWorkProductRef.current = null;
            setNavigationContext({ feature, userStory: story, expandedHierarchy });
            setHierarchyExpandedItems(expandedHierarchy);
            setWorkProductRefreshScope(null);
            setWorkProductRefreshTrigger(previous => previous + 1);
            setShowContextMeshReturn(false);
            setCurrentView("features");
            settleFeatureStudioOpen(hydrationKey, { opened: true });
        };

        void hydrate().catch(error => {
            console.error("Failed to hydrate Feature Studio deep link:", error);
            fail("Unable to load that Feature Studio item. Please try again.");
        });
    }, [dbProjects.length, featureStudioDeepLink, initiatives, projectsStatus, settleFeatureStudioOpen, token]);

    // Initialize data governance watchers
    useDataGovernance(initiatives, selectedProject?.id || null, selectedFeature);

    // Memoized computations for performance optimization
    const selectedProjectId = useMemo(() => selectedProject?.id || null, [selectedProject?.id]);

    const firstFeatureInProject = useMemo(() => {
        return selectedProject && selectedProject.features.length > 0 ? selectedProject.features[0] : null;
    }, [selectedProject]);

    // Validation helpers
    const validateEntityData = useCallback(
        (entityType: "project" | "feature", data: Partial<Initiative | Feature>, existingEntities: Array<Initiative | Feature>): boolean => {
            let validationResult;

            if (entityType === "project") {
                validationResult = validateProject(data as Partial<Initiative>, existingEntities as Initiative[]);
            } else {
                validationResult = validateFeature(data as Partial<Feature>, existingEntities as Feature[]);
            }

            if (!validationResult.isValid) {
                setValidationErrors(prev => ({
                    ...prev,
                    [entityType]: validationResult.errors
                }));
                return false;
            }

            // Clear validation errors
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[entityType];
                return newErrors;
            });

            return true;
        },
        []
    );

    // Optimized view change handler with reduced complexity
    const handleViewChange = useCallback(
        (
            newView: string,
            options?: {
                skipNewProductGuard?: boolean;
                route?: string;
                historyMode?: "push" | "replace";
                preserveRoute?: boolean;
            }
        ) => {
            if (!canAccessAppView(manifest, newView)) {
                if (manifest) setCurrentView(resolveAuthorizedView(manifest, newView));
                return;
            }
            const previousView = currentView;
            if (
                previousView === "new-product" &&
                newView !== "new-product" &&
                !options?.skipNewProductGuard &&
                newProductDraftRef.current?.hasUnsavedChanges &&
                typeof window !== "undefined" &&
                !window.confirm("Leave before the latest product draft changes are saved?")
            ) {
                return;
            }
            if (
                previousView === "new-context-mesh" &&
                newView !== "new-context-mesh" &&
                !options?.skipNewProductGuard &&
                standaloneContextMeshDirtyRef.current &&
                typeof window !== "undefined" &&
                !window.confirm("Leave this Context Mesh draft? It will remain available in Context Mesh Hub so you can continue later.")
            ) {
                return;
            }
            setShowContextMeshReturn(shouldShowContextMeshReturn(previousView, newView));
            setCurrentView(newView);
            if (newView !== "uploads" && newView !== "integrations") {
                setSharedContextMeshTargetId(null);
            }
            if (previousView === "new-context-mesh" && newView !== "new-context-mesh") {
                // The Hub stays mounted while the form is visible, so force it
                // to refetch the draft that autosave just created or updated.
                setContextMeshHubRefreshSignal(value => value + 1);
                setStandaloneContextMeshDraftId(null);
            }
            if (previousView === "new-product" && newView !== "new-product") {
                // The FDE pre-answer is single-use: leaving the screen consumes it so a
                // later manual return (or browser Back) starts with the gate unanswered.
                setNewProductInitialKind(null);
                setNewProductInitialPatch(null);
            }
            if (typeof window !== "undefined") {
                const selectedProductId = resolveNavigationProjectId({
                    selectedProjectId:
                        (selectedProject as any)?.project_id ?? selectedProject?.id,
                    currentProjectId:
                        (currentProjectFromStore as any)?.project_id ??
                        editorDeepLink?.projectId,
                    authProjectId: authDataRef.current.projectId
                });
                const editorWorkProduct = selectedWorkProduct ?? lastKnownWorkProductRef.current;
                const editorRoutes =
                    newView === "editor" &&
                    selectedProductId != null &&
                    editorWorkProduct
                        ? workProductNavigationUrls({
                              projectId: selectedProductId,
                              workProduct: editorWorkProduct,
                              selectedFeature
                          })
                        : null;
                const featureRoute =
                    newView === "features" &&
                    selectedProductId != null
                        ? featureStudioUrl(selectedProductId)
                        : null;
                const managementRoute = managementRouteForView(
                    newView,
                    sharedContextMeshTargetId
                );
                const nextRoute = options?.preserveRoute
                    ? null
                    : options?.route ??
                      managementRoute ??
                      routeForView(newView) ??
                      editorRoutes?.editorUrl ??
                      featureRoute ??
                      (previousView === "new-product" ? "/dashboard" : null);
                const currentRoute = normalizePathWithSearch(
                    window.location.pathname,
                    window.location.search
                );
                if (nextRoute && currentRoute !== nextRoute) {
                    const historyMethod = options?.historyMode === "replace"
                        ? window.history.replaceState
                        : window.history.pushState;
                    const historyState =
                        newView === "editor" && options?.historyMode !== "replace"
                            ? editorHistoryState(newView, currentRoute)
                            : { view: newView };
                    historyMethod.call(window.history, historyState, "", nextRoute);
                    browserLocation.syncFromWindow();
                }
            }
            if (previousView === "editor" && newView !== "editor") {
                dispatch(clearEditorSurface());
                setEditorDeepLink(null);
                deepLinkHydrationStartedRef.current = false;
            }
            setSelectedWorkProduct(prev => {
                const { nextSelectedWorkProduct, nextStash } = resolveSelectedWorkProductOnViewChange({
                    previousView,
                    newView,
                    currentSelectedWorkProduct: prev,
                    stashedWorkProduct: lastKnownWorkProductRef.current
                });
                lastKnownWorkProductRef.current = nextStash;
                return nextSelectedWorkProduct;
            });

            // Fast path for Feature Studio navigation
            if (newView === "features") {
                // Restore navigation context when coming back from editor
                if (previousView === "editor" && navigationContext.feature) {
                    setSelectedFeature(navigationContext.feature);
                    setSelectedUserStory(navigationContext.userStory);
                    // Restore the hierarchy expanded state
                    setHierarchyExpandedItems(navigationContext.expandedHierarchy);
                    // Trigger work product refresh to fetch at the correct level
                    setWorkProductRefreshScope(null);
                    setWorkProductRefreshTrigger(prev => prev + 1);
                    // Clear the context after restoring
                    setNavigationContext({ feature: null, userStory: null, expandedHierarchy: [] });
                }

                // Just expand the selected project in the hierarchy
                if (selectedProject) {
                    setExpandedInitiatives(prev => new Set([...prev, selectedProject.id]));
                }
            }

            // Save navigation context when entering editor
            if (newView === "editor") {
                setNavigationContext({
                    feature: selectedFeature,
                    userStory: selectedUserStory,
                    expandedHierarchy: hierarchyExpandedItems
                });
            }
        },
        [
            dispatch,
            browserLocation.syncFromWindow,
            currentView,
            (currentProjectFromStore as any)?.project_id,
            editorDeepLink?.projectId,
            selectedProject,
            selectedWorkProduct,
            selectedFeature,
            selectedUserStory,
            navigationContext,
            sharedContextMeshTargetId,
            firstFeatureInProject,
            manifest,
            hasProductStudio
        ]
    );

    // Legacy Context Mesh-scoped URLs now enter the shared Files/Integrations
    // surfaces with the standalone mesh preselected. The mesh remains the
    // return destination, but there is no second mesh-only implementation.
    useEffect(() => {
        if (!standaloneContextMeshId || standaloneContextMeshSection === "console") return;
        const nextView = standaloneContextMeshSection === "files" ? "uploads" : "integrations";
        const allowed = standaloneContextMeshSection === "files"
            ? canManageContextMeshFiles
            : canManageContextMeshIntegrations;
        if (!allowed) return;
        setSharedContextMeshTargetId(standaloneContextMeshId);
        setStandaloneContextMeshSection("console");
        handleViewChange(nextView, {
            skipNewProductGuard: true,
            preserveRoute: true
        });
    }, [
        canManageContextMeshFiles,
        canManageContextMeshIntegrations,
        handleViewChange,
        standaloneContextMeshId,
        standaloneContextMeshSection
    ]);

    const returnToStandaloneContextMesh = useCallback(() => {
        if (!sharedContextMeshTargetId) return;
        const contextMeshId = sharedContextMeshTargetId;
        setWorkspaceContextMeshAnalysisRefreshSignal(value => value + 1);
        setStandaloneContextMeshId(contextMeshId);
        setStandaloneContextMeshSection("console");
        setSharedContextMeshTargetId(null);
        setCurrentView("context-mesh-console");
        if (typeof window !== "undefined") {
            window.history.pushState(
                { view: "context-mesh-console", contextMeshId },
                "",
                contextMeshHubUrl(contextMeshId)
            );
        }
    }, [sharedContextMeshTargetId]);

    const returnToProductContextMesh = useCallback(() => {
        setProductContextMeshAnalysisRefreshSignal(value => value + 1);
        handleViewChange("mesh-strength", { skipNewProductGuard: true });
    }, [handleViewChange]);

    const handleCreateFromHierarchy = useCallback(
        ({ project, feature }: { project: any; feature?: any }) => {
            const source = feature ?? project;
            const productName = String(project?.project_name || project?.projectname || project?.name || "").trim();
            const problem = String(
                source?.problem_statement ||
                    source?.problemStatement ||
                    source?.problem ||
                    source?.feature_description ||
                    source?.project_description ||
                    source?.description ||
                    ""
            ).trim();
            const solution = String(
                source?.solution_statement ||
                    source?.solutionStatement ||
                    source?.solution_description ||
                    source?.solution ||
                    ""
            ).trim();

            setNewProductDraftId(null);
            setNewProductInitialKind("existing");
            setNewProductInitialPatch({
                kind: "existing",
                scope: "single",
                productName,
                problemProductSolves: problem,
                solutionProductOffers: solution
            });
            handleViewChange("new-product");
        },
        [handleViewChange]
    );

    // ----- FDE file-intake + return-to-origin wiring (host side) -----------
    // Host-owned UI event bus: created here so the adapters below can push
    // location context; AIChatPanel provides it to ChatProvider via
    // UIEventBusProvider, so the chat and the app shell share one bus.
    const chatBus = useMemo(() => createUIEventBus(), []);

    // handleViewChange re-creates on dep churn; the adapters are built once,
    // so they dispatch through a ref that always holds the freshest closure.
    const viewChangeRef = useRef(handleViewChange);
    useEffect(() => {
        viewChangeRef.current = handleViewChange;
    }, [handleViewChange]);

    // Files routed by the FDE to the Files & Attachments page / New Product
    // screen. Each surface consumes its queue head-first, one file at a time.
    const [uploadsIntakeFiles, setUploadsIntakeFiles] = useState<File[]>([]);
    const [newProductIntakeFiles, setNewProductIntakeFiles] = useState<File[]>([]);
    const fileIntakeGateRef = useRef(createFileIntakeGate());
    const consumeUploadsIntake = useCallback(() => setUploadsIntakeFiles(prev => prev.slice(1)), []);
    const consumeNewProductIntake = useCallback(() => setNewProductIntakeFiles(prev => prev.slice(1)), []);

    const NAVIGABLE_VIEWS = useMemo(
        () => new Set(["projects", "new-product", "refresh-product", "features", "editor", "uploads", "integrations", "profile", "admin"]),
        []
    );
    const navigationAdapter = useMemo<NavigationAdapter>(
        () => ({
            navigate: location => {
                const view =
                    location.route === NEW_PRODUCT_ROUTE
                        ? "new-product"
                        : location.page && NAVIGABLE_VIEWS.has(location.page)
                          ? location.page
                          : location.module === "file-management"
                            ? "uploads"
                            : location.module === "product-hub"
                              ? "projects"
                              : location.module === "feature-studio"
                                ? "features"
                                : location.module === "integrations-console"
                                  ? "integrations"
                                  : null;
                if (!view) return { ok: false, reason: "unknown-location" };
                viewChangeRef.current(view);
                chatBus.pushContext(
                    deriveHostLocationContext(view, typeof window !== "undefined" ? window.location.pathname : undefined)
                );
                return { ok: true };
            }
        }),
        [chatBus, NAVIGABLE_VIEWS]
    );

    const fileIntakeAdapter = useMemo<FileIntakeAdapter>(
        () => ({
            attachToNewProduct: files => {
                if (files.length === 0) return { ok: false, reason: "no-files" };
                const accepted = fileIntakeGateRef.current.filterBatch("new-product", files);
                if (accepted.length === 0) return { ok: true };
                setNewProductIntakeFiles(prev => [...prev, ...accepted]);
                // Case 1 can fire while the chat drove the flow without the
                // standalone screen open yet — bring it up; the screen feeds
                // the queue once its draft loads.
                viewChangeRef.current("new-product");
                chatBus.pushContext(deriveHostLocationContext("new-product", NEW_PRODUCT_ROUTE));
                return { ok: true };
            },
            sendToUploads: files => {
                const accepted = fileIntakeGateRef.current.filterBatch("uploads", files);
                if (accepted.length > 0) {
                    setUploadsIntakeFiles(prev => [...prev, ...accepted]);
                }
                // The shared send_file_to_uploads tool deliberately calls this
                // adapter with an empty array for navigation-only requests. Do
                // not treat that as a no-op: the tool must only report success
                // after the Files & Attachments view has actually been opened.
                viewChangeRef.current("uploads");
                // Push the new location before resolving so the navigation
                // origin tracker attributes this move to the agent.
                chatBus.pushContext(
                    deriveHostLocationContext("uploads", typeof window !== "undefined" ? window.location.pathname : undefined)
                );
                return { ok: true };
            }
        }),
        [chatBus]
    );

    // Fired by UploadCenter after the user saves a scope assignment for an
    // uploaded file: hands the return-to-origin skill its chance to offer a
    // ride back. The skill itself gates on a captured origin, so this is a
    // silent no-op when the user got here on their own.
    const handleUploadsAssignmentComplete = useCallback(() => {
        void chatActionsRef.current?.fireSkill("return-to-origin", { trigger: "uploads-assignment-complete" })?.catch(() => {});
    }, []);
    // -----------------------------------------------------------------------

    const handleHierarchyToggle = useCallback(() => setIsHierarchyCollapsed(!isHierarchyCollapsed), [isHierarchyCollapsed]);

    const handleFeatureSelect = useCallback((feature: Feature | null) => {
        setSelectedFeature(feature);
        if (feature?.id) {
            setLastUsedMatrixId(feature.id);
        }
    }, []);

    const handleEditFeature = useCallback(
        (feature: any) => {
            handleCreateFromHierarchy({ project: selectedProject ?? currentProjectFromStore, feature });
        },
        [currentProjectFromStore, handleCreateFromHierarchy, selectedProject]
    );

    const handleDeleteFeature = useCallback((feature: any) => {
        // Show the AlertDialog instead of window.confirm
        setFeatureToDelete(feature);
        setShowDeleteConfirmDialog(true);
    }, []);

    const confirmDeleteFeature = useCallback(async () => {
        if (!featureToDelete) return;

        const feature = featureToDelete;
        const featureId = feature.featureid || feature.id;

        if (!featureId || !token) {
            console.error("Missing feature ID or token");
            setShowDeleteConfirmDialog(false);
            setFeatureToDelete(null);
            return;
        }

        // Close the dialog and set loading state
        setShowDeleteConfirmDialog(false);
        setDeletingFeatureId(featureId.toString());

        try {
            await deleteFeatureApi(featureId, token);

            // Clear selection if the deleted feature was selected
            if (selectedFeature?.featureid === featureId || selectedFeature?.id === featureId) {
                setSelectedFeature(null);
            }

            // Remove user stories for this feature from Redux state
            // This triggers the hierarchy to show skeleton while recalculating
            dispatch(removeFeatureUserStories(featureId));

            // Optimistically remove the deleted feature from initiatives state immediately
            // This ensures the UI updates before the async refresh completes
            setInitiatives(prev =>
                prev.map(init => ({
                    ...init,
                    features: init.features.filter((f: any) => {
                        const fId = f.featureid || f.id;
                        return fId !== featureId && fId?.toString() !== featureId?.toString();
                    })
                }))
            );

            // Show success notification
            const { toast } = await import("sonner");
            toast.success("Feature deleted successfully", {
                description: `"${feature.name || feature.title}" has been deleted.`
            });

            // After deletion, refresh the features list to ensure sync with backend
            setFeaturesRefreshTrigger(prev => prev + 1);
        } catch (error) {
            console.error("Error deleting feature:", error);
            const { toast } = await import("sonner");
            toast.error("Failed to delete feature", {
                description: error instanceof Error ? error.message : "Unknown error"
            });
            // Only clear loading state on error - on success, it's cleared after refresh completes
            setDeletingFeatureId(null);
        } finally {
            setFeatureToDelete(null);
        }
    }, [featureToDelete, token, selectedFeature, dispatch]);

    const handleEditUserStory = useCallback(
        (feature: any, _userStory: any) => {
            handleCreateFromHierarchy({ project: selectedProject ?? currentProjectFromStore, feature });
        },
        [currentProjectFromStore, handleCreateFromHierarchy, selectedProject]
    );

    const handleDeleteUserStory = useCallback((userStory: any) => {
        // Show the AlertDialog instead of window.confirm
        setUserStoryToDelete(userStory);
        setShowDeleteUserStoryDialog(true);
    }, []);

    const confirmDeleteUserStory = useCallback(async () => {
        if (!userStoryToDelete || !token) {
            setShowDeleteUserStoryDialog(false);
            setUserStoryToDelete(null);
            return;
        }

        const userStory = userStoryToDelete;
        const userStoryId = userStory.id;
        const featureId = userStory.feature_id;

        // Close the dialog
        setShowDeleteUserStoryDialog(false);

        try {
            await dispatch(
                deleteUserStory({
                    userStoryId: userStoryId.toString(),
                    featureId: featureId,
                    idToken: token
                })
            ).unwrap();

            // Clear selection if the deleted user story was selected
            if (selectedUserStory?.id === userStoryId) {
                setSelectedUserStory(null);
            }

            // Show success notification
            const { toast } = await import("sonner");
            toast.success("User story deleted successfully", {
                description: `"${userStory.title}" has been deleted.`
            });
        } catch (error) {
            console.error("Error deleting user story:", error);
            const { toast } = await import("sonner");
            toast.error("Failed to delete user story", {
                description: error instanceof Error ? error.message : "Unknown error"
            });
        } finally {
            setUserStoryToDelete(null);
        }
    }, [userStoryToDelete, token, selectedUserStory, dispatch]);

    const handleInitiativeToggle = useCallback((initiativeId: string) => {
        setExpandedInitiatives(prev => {
            const newSet = new Set(prev);
            if (newSet.has(initiativeId)) {
                newSet.delete(initiativeId);
            } else {
                newSet.add(initiativeId);
            }
            return newSet;
        });
    }, []);

    const handleAccordionToggle = useCallback(
        (accordionId: string) => {
            setExpandedAccordions(prev => {
                const newSet = new Set(prev);
                if (newSet.has(accordionId)) {
                    newSet.delete(accordionId);
                } else {
                    newSet.add(accordionId);
                }
                return newSet;
            });
        },
        [setExpandedAccordions]
    );

    // Enhanced project selection handler - FIXED: Now navigates to Feature Studio
    const handleProjectSelect = useCallback((project: Initiative) => {
        if (project.cardState === "processing" && project.build?.state !== "completed") {
            setRequestedBuildProductId(project.id);
            setShowContextMeshReturn(false);
            setCurrentView("projects");
            return;
        }
        setSelectedProject(project); // Pass full object instead of just ID

        // Clear user story selection when switching projects
        setSelectedUserStory(null);
        setSelectedWorkProduct(null);
        // Different project: the stashed work product (if any) belongs to the
        // old project and must not be resurrected by a later editor restore.
        lastKnownWorkProductRef.current = null;

        // Don't auto-select first feature - show Product/Project level by default
        setSelectedFeature(null);

        // Expand the selected project
        setExpandedInitiatives(prev => new Set([...prev, project.id]));

        // FIXED: Navigate to Feature Studio when project is selected
        setShowContextMeshReturn(false);
        const productId = Number((project as any).project_id ?? project.id);
        handleViewChange("features", {
            route: Number.isSafeInteger(productId) && productId > 0
                ? featureStudioUrl(productId)
                : undefined
        });
    }, [handleViewChange]);

    // FDE navigation must mirror a manual Product Hub selection: persist the
    // target as the current product before mounting Feature Studio. Otherwise
    // Feature Studio's initial project fetch can still return the previously
    // active product and render that stale selection.
    const handleAgentProjectSelect = useCallback(
        (project: Initiative, afterSelect?: () => void) => {
            const finishSelection = () => {
                handleProjectSelect(project);
                afterSelect?.();
            };
            const projectId = Number((project as any).project_id ?? project.id);
            if (!token || !Number.isSafeInteger(projectId) || projectId <= 0) {
                finishSelection();
                return;
            }

            void dispatch(setCurrentProject(projectId))
                .unwrap()
                .catch(error => {
                    console.error("Failed to set FDE navigation target as the current product:", error);
                })
                .finally(finishSelection);
        },
        [dispatch, handleProjectSelect, token]
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onPopState = () => {
            if (
                currentView === "new-product" &&
                newProductDraftRef.current?.hasUnsavedChanges &&
                !window.confirm("Leave before the latest product draft changes are saved?")
            ) {
                window.history.pushState({ view: "new-product" }, "", NEW_PRODUCT_ROUTE);
                return;
            }
            if (
                currentView === "new-context-mesh" &&
                standaloneContextMeshDirtyRef.current &&
                !window.confirm("Leave this Context Mesh draft? It will remain available in Context Mesh Hub so you can continue later.")
            ) {
                window.history.pushState(
                    { view: "new-context-mesh", draftId: standaloneContextMeshDraftId },
                    "",
                    standaloneContextMeshDraftId == null
                        ? NEW_CONTEXT_MESH_ROUTE
                        : contextMeshDraftUrl(standaloneContextMeshDraftId)
                );
                return;
            }
            if (!manifest) return;
            if (parseProductContextMeshDeepLink(window.location.pathname)) {
                const authorizedView = resolveAuthorizedView(manifest, "projects");
                setStandaloneContextMeshId(null);
                setStandaloneContextMeshSection("console");
                setEditorDeepLink(null);
                setFeatureStudioDeepLink(null);
                setShowContextMeshReturn(false);
                setCurrentView(authorizedView);
                window.history.replaceState(
                    { view: authorizedView },
                    "",
                    routeForView(authorizedView) ?? "/dashboard"
                );
                return;
            }
            const nextEditorDeepLink = parseEditorDeepLink(window.location.pathname, window.location.search);
            const nextFeatureStudioDeepLink = parseFeatureStudioDeepLink(window.location.pathname, window.location.search);
            const nextContextMeshDeepLink = parseContextMeshHubDeepLink(window.location.pathname);
            if (
                (currentView === "uploads" || currentView === "integrations") &&
                nextContextMeshDeepLink?.action === "console" &&
                nextContextMeshDeepLink.section === "console"
            ) {
                setWorkspaceContextMeshAnalysisRefreshSignal(value => value + 1);
            }
            setStandaloneContextMeshId(nextContextMeshDeepLink?.action === "console" ? nextContextMeshDeepLink.contextMeshId : null);
            setStandaloneContextMeshDraftId(nextContextMeshDeepLink?.action === "draft" ? nextContextMeshDeepLink.draftId : null);
            setStandaloneContextMeshSection(nextContextMeshDeepLink?.section ?? "console");
            setSharedContextMeshTargetId(
                nextContextMeshDeepLink?.action === "console" &&
                nextContextMeshDeepLink.section !== "console"
                    ? nextContextMeshDeepLink.contextMeshId
                    : null
            );
            const requestedView = viewForPath(window.location.pathname, window.location.search)
                ?? defaultAuthorizedView(manifest);
            const authorizedView = resolveAuthorizedView(manifest, requestedView);
            if (currentView === "new-context-mesh" && authorizedView !== "new-context-mesh") {
                setContextMeshHubRefreshSignal(value => value + 1);
            }
            // Browser back/forward is user-driven navigation: never carry an FDE
            // Product Type pre-answer into a view restored this way.
            setNewProductInitialKind(null);
            setNewProductInitialPatch(null);
            setShowContextMeshReturn(false);
            if (authorizedView === "features" && nextFeatureStudioDeepLink) {
                featureStudioHydrationKeyRef.current = null;
            }
            if (authorizedView === "editor" && nextEditorDeepLink) {
                deepLinkHydrationStartedRef.current = false;
            }
            setEditorDeepLink(
                authorizedView === "editor" ? nextEditorDeepLink : null
            );
            setFeatureStudioDeepLink(
                authorizedView === "features" ? nextFeatureStudioDeepLink : null
            );
            if (currentView === "editor" && authorizedView !== "editor") {
                dispatch(clearEditorSurface());
                if (authorizedView === "features") {
                    setSelectedWorkProduct(previous => {
                        lastKnownWorkProductRef.current = previous;
                        return null;
                    });
                }
            }
            setCurrentView(authorizedView);
            if (authorizedView !== requestedView) {
                window.history.replaceState(
                    { view: authorizedView },
                    "",
                    routeForView(authorizedView) ?? "/dashboard"
                );
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [currentView, dispatch, manifest, standaloneContextMeshDraftId]);

    const openFeatureStudioEntityRef = useCallback((href: string): Promise<FeatureStudioEntityOpenResult> => {
        if (typeof window === "undefined") {
            return Promise.resolve({ opened: false, reason: "Feature Studio navigation is unavailable." });
        }
        const parsed = parseSameOriginFeatureStudioHref(href, window.location.origin);
        if (!parsed) {
            return Promise.resolve({ opened: false, reason: "The Feature Studio link is invalid or cross-origin." });
        }

        const key = featureStudioDeepLinkKey(parsed.link);
        const previous = pendingFeatureStudioOpenRef.current;
        if (previous) {
            clearTimeout(previous.timeoutId);
            previous.resolve({ opened: false, reason: "A newer Feature Studio navigation replaced this request." });
        }

        const opened = new Promise<FeatureStudioEntityOpenResult>(resolve => {
            const timeoutId = setTimeout(() => {
                if (pendingFeatureStudioOpenRef.current?.key !== key) return;
                pendingFeatureStudioOpenRef.current = null;
                resolve({ opened: false, reason: "Feature Studio navigation timed out." });
            }, 30_000);
            pendingFeatureStudioOpenRef.current = { key, resolve, timeoutId };
        });

        window.history.pushState({ view: "features" }, "", parsed.path);
        featureStudioHydrationKeyRef.current = null;
        setSuppressFeatureStudioWelcome(true);
        setFeatureStudioDeepLink(parsed.link);
        setShowContextMeshReturn(false);
        setCurrentView("features");
        return opened;
    }, []);

    // An FDE deep link already explains where it navigated, so suppress the
    // automatic welcome only for that continuous Feature Studio visit. Once
    // the user leaves, a later manual return must show the normal welcome back.
    useEffect(() => {
        if (currentView !== "features" && suppressFeatureStudioWelcome) {
            setSuppressFeatureStudioWelcome(false);
        }
    }, [currentView, suppressFeatureStudioWelcome]);

    // Create a ref to always have the latest auth data - update it IMMEDIATELY
    const authDataRef = React.useRef<{
        projectId: number | null;
        userId: number | null;
        authToken: string | null;
    }>({
        projectId: null,
        userId: null,
        authToken: null
    });

    // Update the ref IMMEDIATELY (not in an effect) to ensure it's always current
    authDataRef.current = {
        projectId: currentProjectFromStore?.project_id || selectedProject?.project_id || (selectedFeature as any)?.projectid || null,
        userId: userProfile?.userid || null,
        authToken: token
    };

    // Handle rebuild start - sets generating state so Feature Studio shows progress indicators
    const handleRebuildStart = useCallback((workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => {
        // Generate composite key matching workProductHandlers pattern
        let workProductKey: string;
        if (userStoryId) {
            workProductKey = `us-${userStoryId}-wp-${workProductId}`;
        } else if (featureId) {
            workProductKey = `f-${featureId}-wp-${workProductId}`;
        } else if (projectId) {
            workProductKey = `p-${projectId}-wp-${workProductId}`;
        } else {
            workProductKey = `wp-${workProductId}`;
        }

        console.log("handleRebuildStart - Setting generating state for:", workProductKey);

        // Add to generating set
        setGeneratingWorkProducts(prev => new Set([...prev, workProductKey]));

        // Set initial progress state
        setWorkProductProgress(prev => {
            const newProgress = new Map(prev);
            newProgress.set(workProductKey, {
                progress: 0,
                message: "Starting rebuild..."
            });
            return newProgress;
        });
    }, []);

    // Handle progress updates during rebuild
    const handleProgressUpdate = useCallback(
        (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string, update?: { progress?: number; message?: string }) => {
            // Generate composite key matching the same pattern as handleRebuildStart
            let workProductKey: string;
            if (userStoryId) {
                workProductKey = `us-${userStoryId}-wp-${workProductId}`;
            } else if (featureId) {
                workProductKey = `f-${featureId}-wp-${workProductId}`;
            } else if (projectId) {
                workProductKey = `p-${projectId}-wp-${workProductId}`;
            } else {
                workProductKey = `wp-${workProductId}`;
            }

            console.log("handleProgressUpdate - Updating progress for:", workProductKey, update);

            setWorkProductProgress(prev => {
                const newProgress = new Map(prev);
                newProgress.set(workProductKey, {
                    progress: update?.progress ?? 0,
                    message: update?.message || "Processing..."
                });
                return newProgress;
            });
        },
        []
    );

    // Handle rebuild completion - removes from generating set
    const handleRebuildComplete = useCallback(
        (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => {
            const latestVersionContextKey = buildDocLatestVersionContextKey(workProductId, projectId, featureId, userStoryId);
            sessionStorage.setItem(latestVersionContextKey, "1");

            // Generate composite key matching the same pattern
            let workProductKey: string;
            if (userStoryId) {
                workProductKey = `us-${userStoryId}-wp-${workProductId}`;
            } else if (featureId) {
                workProductKey = `f-${featureId}-wp-${workProductId}`;
            } else if (projectId) {
                workProductKey = `p-${projectId}-wp-${workProductId}`;
            } else {
                workProductKey = `wp-${workProductId}`;
            }

            console.log("handleRebuildComplete - Removing generating state for:", workProductKey);

            // Remove from generating set
            setGeneratingWorkProducts(prev => {
                const newSet = new Set(prev);
                newSet.delete(workProductKey);
                return newSet;
            });

            // Clear progress state
            setWorkProductProgress(prev => {
                const newProgress = new Map(prev);
                newProgress.delete(workProductKey);
                return newProgress;
            });

            // Refresh the rebuild hierarchy to update the UI with new rebuild states
            // Get the project ID from the callback params or from the current context
            const effectiveProjectId = projectId || currentProjectFromStore?.project_id || selectedProject?.project_id || (selectedFeature as any)?.projectid;
            if (effectiveProjectId) {
                console.log("handleRebuildComplete - Refreshing rebuild hierarchy for project:", effectiveProjectId);
                dispatch(fetchProjectRebuildHierarchy(Number(effectiveProjectId)));

                // Also refresh work products to update the needs_rebuild flag on work product cards
                // Determine the correct level based on what context the rebuild was for
                if (userStoryId) {
                    console.log("handleRebuildComplete - Refreshing work products at USERSTORY level for:", userStoryId);
                    dispatch(
                        fetchWorkProductsByPhase({
                            projectId: Number(effectiveProjectId),
                            level: "USERSTORY",
                            featureId: featureId ? Number(featureId) : undefined,
                            userStoryId: userStoryId
                        })
                    );
                } else if (featureId) {
                    console.log("handleRebuildComplete - Refreshing work products at FEATURE level for:", featureId);
                    dispatch(
                        fetchWorkProductsByPhase({
                            projectId: Number(effectiveProjectId),
                            level: "FEATURE",
                            featureId: Number(featureId)
                        })
                    );
                } else {
                    console.log("handleRebuildComplete - Refreshing work products at PRODUCT level");
                    dispatch(
                        fetchWorkProductsByPhase({
                            projectId: Number(effectiveProjectId),
                            level: "PRODUCT"
                        })
                    );
                }
            }
        },
        [dispatch, currentProjectFromStore?.project_id, selectedProject?.project_id, selectedFeature]
    );

    const openWorkProductEditor = useCallback(
        (workProduct: WorkProduct) => {
            const projectId = resolveNavigationProjectId({
                selectedProjectId:
                    (selectedProject as any)?.project_id ?? selectedProject?.id,
                currentProjectId: (currentProjectFromStore as any)?.project_id,
                authProjectId: authDataRef.current.projectId
            });
            const navigation = projectId != null
                ? workProductNavigationUrls({
                      projectId,
                      workProduct,
                      selectedFeature
                  })
                : null;
            setEditorDeepLink(null);
            deepLinkHydrationStartedRef.current = false;
            handleViewChange("editor", {
                route: navigation?.editorUrl
            });
        },
        [
            (currentProjectFromStore as any)?.project_id,
            handleViewChange,
            selectedFeature,
            selectedProject
        ]
    );

    // Create handler groups with dependency optimization and improved object passing
    const workProductHandlers = useMemo(
        () =>
            createWorkProductHandlers(
                selectedFeature,
                selectedUserStory,
                initiatives,
                setSelectedWorkProduct,
                openWorkProductEditor,
                setLastUsedMatrixId,
                setGeneratingWorkProducts,
                setProcessingWorkProducts,
                setWorkProductProgress,
                setWorkProductsByPhase,
                setExpandedWorkProducts,
                workProductProgress,
                // Pass a getter function that always returns current values from the ref
                () => {
                    console.log("getCurrentAuthData called, returning:", authDataRef.current);
                    return authDataRef.current;
                },
                // Pass a getter function to get current work products by phase
                () => workProductsByPhase
            ),
        [selectedFeature, selectedUserStory, initiatives, openWorkProductEditor, workProductProgress, workProductsByPhase] // Added workProductsByPhase dependency
    );

    const normalizeLookupText = useCallback((value: unknown): string => String(value ?? "").trim().toLowerCase(), []);

    const extractWorkProductTargetFromElementId = useCallback((elementId?: string): { workProductId?: string; phase?: string; workProductName?: string } => {
        const raw = String(elementId ?? "").trim();
        if (!raw.startsWith("wp-")) return {};
        const match = raw.match(/^wp-.+-(discovery|specify|design|plan|task-breakdown|task_breakdown|code|implement|qa|validate|deploy)-(.+)$/i);
        if (!match) return {};
        const suffix = match[2]?.trim();
        const numericId = suffix && /^[0-9]+$/.test(suffix) ? suffix : undefined;
        const inferredName = numericId ? undefined : suffix?.replace(/-/g, " ").trim();
        return {
            phase: match[1].toLowerCase(),
            workProductId: numericId,
            workProductName: inferredName || undefined
        };
    }, []);

    const normalizePhaseForGeneration = useCallback(
        (phase?: string): string => {
            const normalized = normalizeLookupText(phase);
            const candidate =
                normalized === "task_breakdown" || normalized === "task breakdown"
                    ? "task-breakdown"
                    : normalized === "plan"
                      ? "design"
                      : normalized === "specify"
                        ? "discovery"
                        : normalized === "implement"
                          ? "code"
                          : normalized === "validate"
                            ? "qa"
                            : normalized;
            const allowed = ["discovery", "design", "task-breakdown", "code", "qa", "deploy"];
            if (allowed.includes(candidate)) return candidate;
            const normalizedCurrentPhase = normalizeLookupText(featureStudioTab);
            const currentPhase = normalizedCurrentPhase === "task_breakdown" || normalizedCurrentPhase === "task breakdown"
                ? "task-breakdown"
                : normalizedCurrentPhase;
            return allowed.includes(currentPhase) ? currentPhase : "discovery";
        },
        [featureStudioTab, normalizeLookupText]
    );

    const mapPhaseToResolverHint = useCallback((phase?: string): "SPECIFY" | "PLAN" | "TASK_BREAKDOWN" | "IMPLEMENT" | "VALIDATE" => {
        const normalized = normalizePhaseForGeneration(phase);
        switch (normalized) {
            case "discovery":
                return "SPECIFY";
            case "design":
                return "PLAN";
            case "task-breakdown":
                return "TASK_BREAKDOWN";
            case "qa":
            case "deploy":
                return "VALIDATE";
            case "code":
            default:
                return "IMPLEMENT";
        }
    }, [normalizePhaseForGeneration]);

    const resolveCurrentGenerationScope = useCallback((): ResolverScope => {
        const selectedFeatureId = Number((selectedFeature as any)?.id ?? (selectedFeature as any)?.featureid);
        const normalizedFeatureId = Number.isFinite(selectedFeatureId) && selectedFeatureId > 0
            ? selectedFeatureId
            : undefined;
        const selectedStoryId = selectedUserStory?.id ? String(selectedUserStory.id) : undefined;
        if (selectedStoryId) {
            return {
                level: "USERSTORY",
                ...(normalizedFeatureId ? { featureId: normalizedFeatureId } : {}),
                userStoryId: selectedStoryId
            };
        }
        if (normalizedFeatureId) {
            return {
                level: "FEATURE",
                featureId: normalizedFeatureId
            };
        }
        return { level: "PRODUCT" };
    }, [selectedFeature, selectedUserStory?.id]);

    const normalizeResolverScope = useCallback((scope?: PendingWorkProductGenerationRequest["scope"]): ResolverScope => {
        if (!scope) return resolveCurrentGenerationScope();
        const featureId = typeof scope.featureId === "number" && scope.featureId > 0 ? scope.featureId : undefined;
        const userStoryId = scope.userStoryId ? String(scope.userStoryId) : undefined;
        if (scope.level === "USERSTORY") {
            return {
                level: "USERSTORY",
                ...(featureId ? { featureId } : {}),
                ...(userStoryId ? { userStoryId } : {})
            };
        }
        if (scope.level === "FEATURE") {
            return {
                level: "FEATURE",
                ...(featureId ? { featureId } : {})
            };
        }
        return { level: "PRODUCT" };
    }, [resolveCurrentGenerationScope]);

    const workProductKeyForScope = useCallback(
        (workProductId: string, projectId: number | undefined, scope: PendingWorkProductGenerationRequest["scope"]) => {
            if (scope?.level === "USERSTORY" && scope.userStoryId) {
                return `us-${scope.userStoryId}-wp-${workProductId}`;
            }
            if (scope?.level === "FEATURE" && scope.featureId) {
                return `f-${scope.featureId}-wp-${workProductId}`;
            }
            if (projectId) {
                return `p-${projectId}-wp-${workProductId}`;
            }
            return `wp-${workProductId}`;
        },
        []
    );

    const markWorkProductReadyLocally = useCallback(
        (
            workProductId: string | undefined,
            workProductName: string | undefined,
            content: string,
            scope: PendingWorkProductGenerationRequest["scope"]
        ) => {
            const isScopeMatch = (workProduct: WorkProduct): boolean => {
                if (!scope?.level) return true;

                const candidateScope = String(workProduct.scope_level || workProduct.level || "").toUpperCase();
                const candidateFeatureId = Number(workProduct.feature_id || 0) || null;
                const candidateStoryId = workProduct.user_story_id ? String(workProduct.user_story_id) : null;

                if (candidateScope === "PRODUCT") {
                    return scope.level === "PRODUCT";
                }

                if (candidateScope === "FEATURE") {
                    return scope.level === "FEATURE" && Number(scope.featureId || 0) === Number(candidateFeatureId || 0);
                }

                if (candidateScope === "USERSTORY") {
                    return (
                        scope.level === "USERSTORY" &&
                        Number(scope.featureId || 0) === Number(candidateFeatureId || 0) &&
                        String(scope.userStoryId || "") === String(candidateStoryId || "")
                    );
                }

                if (scope.level === "PRODUCT") return !candidateFeatureId && !candidateStoryId;
                if (scope.level === "FEATURE") {
                    return Number(scope.featureId || 0) === Number(candidateFeatureId || 0) && !candidateStoryId;
                }

                return (
                    Number(scope.featureId || 0) === Number(candidateFeatureId || 0) &&
                    String(scope.userStoryId || "") === String(candidateStoryId || "")
                );
            };

            const normalizedId = String(workProductId ?? "").trim();
            const normalizedName = normalizeWorkProductLookup(workProductName);
            setWorkProductsByPhase(prev => {
                let changed = false;
                const updated: Record<string, WorkProduct[]> = {};
                for (const [phase, workProducts] of Object.entries(prev)) {
                    let phaseChanged = false;
                    const nextPhase = workProducts.map(workProduct => {
                        const idMatches = Boolean(normalizedId) && String(workProduct.id) === normalizedId;
                        const nameMatches = Boolean(normalizedName) && normalizeWorkProductLookup(workProduct.name) === normalizedName;
                        if (!idMatches && !nameMatches) return workProduct;
                        if (!isScopeMatch(workProduct)) return workProduct;
                        phaseChanged = true;
                        changed = true;
                        return {
                            ...workProduct,
                            state: "editable" as const,
                            hasContent: true,
                            content: content || workProduct.content,
                            level: scope?.level || workProduct.level,
                            feature_id: scope?.level === "PRODUCT" ? undefined : (scope?.featureId ?? workProduct.feature_id),
                            user_story_id:
                                scope?.level === "USERSTORY"
                                    ? (scope.userStoryId ?? workProduct.user_story_id)
                                    : undefined
                        };
                    });
                    updated[phase] = phaseChanged ? nextPhase : workProducts;
                }
                return changed ? updated : prev;
            });
        },
        []
    );

    const resolveWorkProductIdentityForTracking = useCallback(
        (
            request: PendingWorkProductGenerationRequest,
            resolved?: { workProductId?: string; workProductName?: string }
        ): {
            workProductId?: string;
            workProductName?: string;
            scope?: { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null };
        } => {
            const ids = [resolved?.workProductId, request.workProductId]
                .map(value => String(value ?? "").trim())
                .filter(Boolean);
            const names = [resolved?.workProductName, request.workProductName]
                .map(value => String(value ?? "").trim())
                .filter(Boolean);

            const deriveScopeFromCatalog = (
                matched: WorkProduct
            ): { level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId: number | null; userStoryId: string | null } | undefined => {
                const declaredScope = String(matched.scope_level || matched.level || "").toUpperCase();
                if (declaredScope === "PRODUCT") return { level: "PRODUCT", featureId: null, userStoryId: null };
                if (declaredScope === "FEATURE") {
                    const featureId = Number(matched.feature_id || request.scope?.featureId || 0) || null;
                    return featureId ? { level: "FEATURE", featureId, userStoryId: null } : undefined;
                }
                if (declaredScope === "USERSTORY") {
                    const featureId = Number(matched.feature_id || request.scope?.featureId || 0) || null;
                    const userStoryId = String(matched.user_story_id || request.scope?.userStoryId || "").trim();
                    return featureId && userStoryId ? { level: "USERSTORY", featureId, userStoryId } : undefined;
                }

                const levels = Array.isArray(matched.display_level)
                    ? matched.display_level.map(level => String(level || "").toUpperCase())
                    : [];

                if (levels.length === 1) {
                    if (levels[0] === "PRODUCT") return { level: "PRODUCT", featureId: null, userStoryId: null };
                    if (levels[0] === "FEATURE") {
                        const featureId = Number(request.scope?.featureId || matched.feature_id || 0) || null;
                        return featureId ? { level: "FEATURE", featureId, userStoryId: null } : undefined;
                    }
                    if (levels[0] === "USERSTORY") {
                        const featureId = Number(request.scope?.featureId || matched.feature_id || 0) || null;
                        const userStoryId = String(request.scope?.userStoryId || matched.user_story_id || "").trim();
                        return featureId && userStoryId ? { level: "USERSTORY", featureId, userStoryId } : undefined;
                    }
                }

                if (request.scope?.level && levels.includes(String(request.scope.level).toUpperCase())) {
                    if (request.scope.level === "PRODUCT") return { level: "PRODUCT", featureId: null, userStoryId: null };
                    if (request.scope.level === "FEATURE") {
                        return request.scope.featureId ? { level: "FEATURE", featureId: request.scope.featureId, userStoryId: null } : undefined;
                    }
                    if (request.scope.level === "USERSTORY") {
                        return request.scope.featureId && request.scope.userStoryId
                            ? { level: "USERSTORY", featureId: request.scope.featureId, userStoryId: request.scope.userStoryId }
                            : undefined;
                    }
                }

                if (levels.includes("PRODUCT")) {
                    return { level: "PRODUCT", featureId: null, userStoryId: null };
                }

                return request.scope;
            };

            const catalog = Object.values(workProductsByPhase).flat();
            const byId = ids.length > 0
                ? catalog.find(workProduct => ids.includes(String(workProduct.id)))
                : undefined;
            if (byId) {
                return {
                    workProductId: String(byId.id),
                    workProductName: byId.name,
                    scope: deriveScopeFromCatalog(byId)
                };
            }

            const normalizedNames = names.map(normalizeWorkProductLookup);
            const byName = normalizedNames.length > 0
                ? catalog.find(workProduct => normalizedNames.includes(normalizeWorkProductLookup(workProduct.name)))
                : undefined;
            if (byName) {
                return {
                    workProductId: String(byName.id),
                    workProductName: byName.name,
                    scope: deriveScopeFromCatalog(byName)
                };
            }

            return {
                workProductId: ids[0],
                workProductName: names[0],
                scope: request.scope
            };
        },
        [workProductsByPhase]
    );

    const mergeWorkProductPhaseMaps = useCallback(
        (...maps: Array<Record<string, WorkProduct[]> | null | undefined>): Record<string, WorkProduct[]> => {
            const merged: Record<string, WorkProduct[]> = {};
            const seenByPhase = new Map<string, Set<string>>();

            for (const map of maps) {
                if (!map) continue;
                for (const [phase, workProducts] of Object.entries(map)) {
                    if (!Array.isArray(workProducts) || workProducts.length === 0) continue;
                    if (!merged[phase]) {
                        merged[phase] = [];
                        seenByPhase.set(phase, new Set<string>());
                    }
                    const phaseSeen = seenByPhase.get(phase)!;
                    for (const workProduct of workProducts) {
                        const key = String(workProduct.id);
                        if (phaseSeen.has(key)) continue;
                        phaseSeen.add(key);
                        merged[phase].push(workProduct);
                    }
                }
            }

            return merged;
        },
        []
    );

    const refreshWorkProductsForGenerationRequest = useCallback(
        async (
            request: PendingWorkProductGenerationRequest,
            options?: { resolvedScope?: PendingWorkProductGenerationRequest["scope"] }
        ): Promise<Record<string, WorkProduct[]> | null> => {
            const effectiveProjectId =
                (currentProjectFromStore as any)?.project_id || (selectedProject as any)?.project_id || (selectedFeature as any)?.projectid;
            if (!effectiveProjectId) return null;

            const selectedFeatureId = Number((selectedFeature as any)?.id ?? (selectedFeature as any)?.featureid);
            const fallbackFeatureId = Number.isFinite(selectedFeatureId) && selectedFeatureId > 0 ? selectedFeatureId : undefined;
            const fallbackStoryId = selectedUserStory?.id ? String(selectedUserStory.id) : undefined;

            const currentScope = resolveCurrentGenerationScope();
            const requestedScope = request.scope ? normalizeResolverScope(request.scope) : undefined;
            const resolvedScope = options?.resolvedScope ? normalizeResolverScope(options.resolvedScope) : undefined;

            const withFallbackScopeIds = (scope?: ResolverScope): ResolverScope | undefined => {
                if (!scope) return scope;
                if (scope.level === "FEATURE") {
                    return {
                        ...scope,
                        ...(scope.featureId ? {} : fallbackFeatureId ? { featureId: fallbackFeatureId } : {})
                    };
                }
                if (scope.level === "USERSTORY") {
                    const canonicalStoryId = scope.userStoryId && isLikelyCanonicalStoryId(scope.userStoryId)
                        ? scope.userStoryId
                        : undefined;
                    return {
                        ...scope,
                        ...(scope.featureId ? {} : fallbackFeatureId ? { featureId: fallbackFeatureId } : {}),
                        ...(canonicalStoryId ? { userStoryId: canonicalStoryId } : fallbackStoryId ? { userStoryId: fallbackStoryId } : {})
                    };
                }
                return scope;
            };

            const scopesToFetch: Array<{ level: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId?: number; userStoryId?: string }> = [];
            const pushScope = (scope?: ResolverScope) => {
                if (!scope) return;

                if (scope.level === "USERSTORY") {
                    if (!scope.featureId || !scope.userStoryId) return;
                    const key = `USERSTORY:${scope.featureId}:${scope.userStoryId}`;
                    if (scopesToFetch.some(existing => `USERSTORY:${existing.featureId}:${existing.userStoryId}` === key)) return;
                    scopesToFetch.push({ level: "USERSTORY", featureId: scope.featureId, userStoryId: scope.userStoryId });
                    return;
                }

                if (scope.level === "FEATURE") {
                    if (!scope.featureId) return;
                    const key = `FEATURE:${scope.featureId}`;
                    if (scopesToFetch.some(existing => `FEATURE:${existing.featureId}` === key)) return;
                    scopesToFetch.push({ level: "FEATURE", featureId: scope.featureId });
                    return;
                }

                if (scopesToFetch.some(existing => existing.level === "PRODUCT")) return;
                scopesToFetch.push({ level: "PRODUCT" });
            };

            const hydratedCurrentScope = withFallbackScopeIds(currentScope);
            const hydratedRequestedScope = withFallbackScopeIds(requestedScope);
            const hydratedResolvedScope = withFallbackScopeIds(resolvedScope);

            pushScope(hydratedCurrentScope);
            if (hydratedCurrentScope?.level === "USERSTORY") pushScope(withFallbackScopeIds({ level: "FEATURE", featureId: hydratedCurrentScope.featureId }));
            if (hydratedCurrentScope?.level !== "PRODUCT") pushScope({ level: "PRODUCT" });
            pushScope(hydratedRequestedScope);
            pushScope(hydratedResolvedScope);

            const fetchedCatalogs: Array<Record<string, WorkProduct[]>> = [];

            try {
                for (const scope of scopesToFetch) {
                    if (scope.level === "USERSTORY") {
                        fetchedCatalogs.push(
                            await dispatch(
                            fetchWorkProductsByPhase({
                                projectId: Number(effectiveProjectId),
                                featureId: Number(scope.featureId),
                                userStoryId: String(scope.userStoryId),
                                level: "USERSTORY"
                            })
                        ).unwrap()
                        );
                        continue;
                    }

                    if (scope.level === "FEATURE") {
                        fetchedCatalogs.push(
                            await dispatch(
                            fetchWorkProductsByPhase({
                                projectId: Number(effectiveProjectId),
                                featureId: Number(scope.featureId),
                                level: "FEATURE"
                            })
                        ).unwrap()
                        );
                        continue;
                    }

                    fetchedCatalogs.push(
                        await dispatch(
                        fetchWorkProductsByPhase({
                            projectId: Number(effectiveProjectId),
                            level: "PRODUCT"
                        })
                    ).unwrap()
                    );
                }

                const merged = mergeWorkProductPhaseMaps(...fetchedCatalogs);
                if (Object.keys(merged).length > 0) {
                    setWorkProductsByPhase(merged);
                }
                return merged;
            } catch (error) {
                console.error("[AIChatPanel] generate_work_product: unable to refresh scoped work products", error);
                return null;
            }
        },
        [
            (currentProjectFromStore as any)?.project_id,
            dispatch,
            mergeWorkProductPhaseMaps,
            normalizeResolverScope,
            resolveCurrentGenerationScope,
            selectedFeature,
            (selectedProject as any)?.project_id,
            selectedUserStory?.id
        ]
    );

    const trackResolverGenerationJob = useCallback(
        (
            jobId: string,
            request: PendingWorkProductGenerationRequest,
            resolved?: { workProductId?: string; workProductName?: string; scope?: { level?: "PRODUCT" | "FEATURE" | "USERSTORY"; featureId?: number | null; userStoryId?: string | null } }
        ) => {
            const authToken = authDataRef.current.authToken;
            if (!authToken) return;

            const effectiveProjectId =
                (currentProjectFromStore as any)?.project_id || (selectedProject as any)?.project_id || (selectedFeature as any)?.projectid;
            const identity = resolveWorkProductIdentityForTracking(request, resolved);
            const workProductId = String(identity.workProductId ?? "").trim();
            const workProductName = String(identity.workProductName ?? "").trim();
            const hasTrackableKey = Boolean(workProductId);

            const resolvedScope = resolved?.scope?.level
                ? {
                    level: resolved.scope.level,
                    featureId: typeof resolved.scope.featureId === "number" ? resolved.scope.featureId : null,
                    userStoryId: resolved.scope.userStoryId ? String(resolved.scope.userStoryId) : null
                }
                : identity.scope || request.scope;

            const workProductKey = hasTrackableKey
                ? workProductKeyForScope(
                    workProductId,
                    effectiveProjectId ? Number(effectiveProjectId) : undefined,
                    resolvedScope
                )
                : null;

            if (workProductKey) {
                setGeneratingWorkProducts(prev => new Set([...prev, workProductKey]));
                setWorkProductProgress(prev => {
                    const next = new Map(prev);
                    next.set(workProductKey, { progress: 0, message: "Starting generation..." });
                    return next;
                });
            }

            getJobStatusManager().registerJob(jobId, authToken, {
                onUpdate: (update: JobStreamUpdate) => {
                    if (!workProductKey) return;
                    setWorkProductProgress(prev => {
                        const next = new Map(prev);
                        next.set(workProductKey, {
                            progress: update.progress ?? 0,
                            message: update.message || "Processing..."
                        });
                        return next;
                    });
                },
                onComplete: (update: JobStreamUpdate) => {
                    if (workProductKey) {
                        setGeneratingWorkProducts(prev => {
                            const next = new Set(prev);
                            next.delete(workProductKey);
                            return next;
                        });
                        setWorkProductProgress(prev => {
                            const next = new Map(prev);
                            next.delete(workProductKey);
                            return next;
                        });
                    }

                    if (workProductId || workProductName) {
                        markWorkProductReadyLocally(
                            workProductId,
                            workProductName,
                            String(update.result || update.output || "Work product generated successfully"),
                            resolvedScope
                        );
                    }
                    setWorkProductRefreshScope(resolvedScope ?? request.scope ?? null);
                    setWorkProductRefreshTrigger(prev => prev + 1);
                    void refreshWorkProductsForGenerationRequest(request, { resolvedScope });
                },
                onError: (error: Error) => {
                    if (workProductKey) {
                        setGeneratingWorkProducts(prev => {
                            const next = new Set(prev);
                            next.delete(workProductKey);
                            return next;
                        });
                        setWorkProductProgress(prev => {
                            const next = new Map(prev);
                            next.delete(workProductKey);
                            return next;
                        });
                    }
                    toast.error(`Work product generation failed: ${error.message}`);
                }
            });
        },
        [
            currentProjectFromStore,
            resolveWorkProductIdentityForTracking,
            markWorkProductReadyLocally,
            refreshWorkProductsForGenerationRequest,
            selectedFeature,
            selectedProject,
            setWorkProductRefreshScope,
            setWorkProductRefreshTrigger,
            workProductKeyForScope
        ]
    );

    const tryResolveAndGenerateViaBackend = useCallback(
        async (request: PendingWorkProductGenerationRequest) => {
            const authToken = authDataRef.current.authToken;
            const effectiveProjectId =
                (currentProjectFromStore as any)?.project_id || (selectedProject as any)?.project_id || (selectedFeature as any)?.projectid;
            if (!authToken || !effectiveProjectId) return null;
            if (!request.workProductName && !request.workProductId) return null;

            const phase = normalizePhaseForGeneration(request.phase);
            const preferredPhaseWorkProducts = workProductsByPhase[phase] || [];
            const allPhaseWorkProducts = Object.values(workProductsByPhase).flat();
            const targetForScope =
                findWorkProductGenerationSource(preferredPhaseWorkProducts, request) ??
                findWorkProductGenerationSource(allPhaseWorkProducts, request);
            const closestTargetForScope =
                !targetForScope && request.workProductName
                    ? findClosestWorkProductGenerationSource(allPhaseWorkProducts, request.workProductName)
                    : undefined;

            const resolveScopeFromTarget = (
                target: WorkProduct,
                requestedScope?: PendingWorkProductGenerationRequest["scope"]
            ): PendingWorkProductGenerationRequest["scope"] | undefined => {
                if (!requestedScope) return requestedScope;

                const supportedLevels = new Set<"PRODUCT" | "FEATURE" | "USERSTORY">();
                const declaredScope = String(target.scope_level || target.level || "").trim().toUpperCase();
                if (declaredScope === "PRODUCT" || declaredScope === "FEATURE" || declaredScope === "USERSTORY") {
                    supportedLevels.add(declaredScope);
                }

                if (Array.isArray(target.display_level)) {
                    target.display_level
                        .map(level => String(level || "").trim().toUpperCase())
                        .forEach(level => {
                            if (level === "PRODUCT" || level === "FEATURE" || level === "USERSTORY") supportedLevels.add(level);
                        });
                }

                if (supportedLevels.size === 0) return requestedScope;

                if (requestedScope.level === "USERSTORY" && !supportedLevels.has("USERSTORY")) return requestedScope;
                if (requestedScope.level === "FEATURE" && !supportedLevels.has("FEATURE")) return requestedScope;

                const preferredOrder: Array<"PRODUCT" | "FEATURE" | "USERSTORY"> =
                    requestedScope.level === "USERSTORY"
                        ? ["USERSTORY", "FEATURE", "PRODUCT"]
                        : requestedScope.level === "FEATURE"
                          ? ["FEATURE", "PRODUCT"]
                          : ["PRODUCT"];

                const resolvedLevel = preferredOrder.find(level => supportedLevels.has(level));
                if (!resolvedLevel) return requestedScope;

                if (resolvedLevel === "PRODUCT") return { level: "PRODUCT", featureId: null, userStoryId: null };

                const featureId =
                    requestedScope.featureId || Number((selectedFeature as any)?.id || (selectedFeature as any)?.featureid || 0) || null;
                if (resolvedLevel === "FEATURE") {
                    if (!featureId) return requestedScope;
                    return { level: "FEATURE", featureId, userStoryId: null };
                }

                const userStoryId = requestedScope.userStoryId || (selectedUserStory?.id ? String(selectedUserStory.id) : null);
                if (!featureId || !userStoryId) {
                    return supportedLevels.has("FEATURE") && featureId
                        ? { level: "FEATURE", featureId, userStoryId: null }
                        : requestedScope;
                }

                return { level: "USERSTORY", featureId, userStoryId };
            };

            const scopeTarget = targetForScope;
            const hasExplicitTargetContext =
                Boolean(String(request.targetFeatureName ?? "").trim()) ||
                Boolean(String(request.targetUserStoryName ?? "").trim()) ||
                Boolean(String(request.targetUserStoryNumber ?? "").trim());
            const effectiveRequestScope = hasExplicitTargetContext
                ? request.scope
                : scopeTarget
                    ? resolveScopeFromTarget(scopeTarget, request.scope)
                    : request.scope;

            console.info("[AIChatPanel] resolve-and-generate request context", {
                requestedName: request.workProductName,
                requestedId: request.workProductId,
                requestedScope: request.scope,
                currentScope: resolveCurrentGenerationScope(),
                inferredScopeTarget: scopeTarget
                    ? {
                          id: String(scopeTarget.id),
                          name: scopeTarget.name,
                          variable: scopeTarget.variable,
                          scope_level: scopeTarget.scope_level,
                          display_level: scopeTarget.display_level,
                      }
                    : null,
                effectiveRequestScope,
            });

            try {
                const currentScope = resolveCurrentGenerationScope();
                const requestedScope = effectiveRequestScope ? normalizeResolverScope(effectiveRequestScope) : undefined;
                const targetFeatureName = String(request.targetFeatureName ?? "").trim();
                const targetUserStoryName = String(request.targetUserStoryName ?? "").trim();
                const targetUserStoryNumber = String(request.targetUserStoryNumber ?? "").trim();
                const hasExplicitTargetContext =
                    Boolean(targetFeatureName) ||
                    Boolean(targetUserStoryName) ||
                    Boolean(targetUserStoryNumber);
                const requestedScopeForResolver = (() => {
                    if (!requestedScope) return undefined;
                    if (hasExplicitTargetContext) return undefined;
                    if (
                        requestedScope.level === "USERSTORY" &&
                        requestedScope.userStoryId &&
                        !isLikelyCanonicalStoryId(requestedScope.userStoryId)
                    ) {
                        return undefined;
                    }
                    return requestedScope;
                })();
                const selectedScopeFeatureName =
                    currentScope?.level === "FEATURE" || currentScope?.level === "USERSTORY"
                        ? ((selectedFeature?.title ?? selectedFeature?.name ?? null) as string | null)
                        : null;
                const selectedScopeUserStoryName =
                    currentScope?.level === "USERSTORY"
                        ? ((selectedUserStory?.title ?? selectedUserStory?.name ?? null) as string | null)
                        : null;
                const selectedScopeUserStoryNumber =
                    currentScope?.level === "USERSTORY" && selectedUserStory?.id
                        ? String(selectedUserStory.id)
                        : null;
                const targetContext = {
                    featureName: targetFeatureName || selectedScopeFeatureName,
                    userStoryName: targetUserStoryName || selectedScopeUserStoryName,
                    userStoryNumber: targetUserStoryNumber || selectedScopeUserStoryNumber,
                };
                const requestTextParts = [request.workProductName ? `Generate ${request.workProductName}` : null];
                if (targetContext.featureName) requestTextParts.push(`for the feature ${targetContext.featureName}`);
                if (targetContext.userStoryName) requestTextParts.push(`with user story ${targetContext.userStoryName}`);
                const requestText = requestTextParts.filter(Boolean).join(" ");
                const response = await fetchWithAuth("/api/workproducts/resolve-and-generate", authToken, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-WorkProduct-Contract-Version": WORK_PRODUCT_RESOLVER_CONTRACT_VERSION
                    },
                    body: JSON.stringify({
                        projectId: Number(effectiveProjectId),
                        ...(request.workProductName ? { requestedName: request.workProductName } : {}),
                        ...(request.workProductId ? { workProductId: request.workProductId } : {}),
                        ...(request.confirmationToken ? { confirmationToken: request.confirmationToken } : {}),
                        ...(typeof request.forceNewVersion === "boolean" ? { forceNewVersion: request.forceNewVersion } : {}),
                        resolutionContext: {
                            currentScope,
                            ...(requestedScopeForResolver ? { requestedScope: requestedScopeForResolver } : {}),
                            targetContext,
                            scopeResolutionMode: "DETERMINISTIC_FIRST",
                            phaseHint: mapPhaseToResolverHint(request.phase),
                            ...(requestText ? { requestText } : {})
                        }
                    })
                });

                if (!response.ok) {
                    let bodyText: string | null = null;
                    try {
                        bodyText = await response.text();
                    } catch {
                        bodyText = null;
                    }
                    console.warn("[AIChatPanel] resolve-and-generate non-ok response", {
                        status: response.status,
                        statusText: response.statusText,
                        body: bodyText,
                    });
                    return null;
                }

                const payload = await response.json() as WorkProductResolverResponse;
                console.info("[AIChatPanel] resolve-and-generate response", payload);
                if (payload.status === "resolved_and_started") {
                    if (!payload.jobId) return null;
                    trackResolverGenerationJob(payload.jobId, { ...request, scope: effectiveRequestScope }, payload.resolved);
                    return {
                        ok: true as const,
                        requestId: payload.requestId,
                        jobId: payload.jobId,
                        workProductId: payload.resolved?.workProductId,
                        workProductName: payload.resolved?.workProductName,
                        message: payload.message || `Started generating ${payload.resolved?.workProductName || request.workProductName || request.workProductId}.`
                    };
                }

                if (payload.status === "confirmation_required") {
                    const backendConfirmationToken = payload.confirmationToken || request.confirmationToken;
                    const mappedCandidates: WorkProductConfirmationCandidate[] = Array.isArray(payload.candidates)
                        ? payload.candidates
                            .map((candidate, index) => {
                                const workProductId = candidate.workProduct?.id;
                                const workProductName = candidate.workProduct?.name;
                                if (!workProductId || !workProductName || index > 2) return null;
                                const scopeLevel = candidate.scope?.level;
                                return {
                                    option: (index + 1) as 1 | 2 | 3,
                                    rank: candidate.rank,
                                    confidence: candidate.confidence,
                                    workProduct: {
                                        id: workProductId,
                                        name: workProductName,
                                    },
                                    ...(scopeLevel
                                        ? {
                                            scope: {
                                                level: scopeLevel,
                                                featureId: candidate.scope?.featureId ?? null,
                                                userStoryId: candidate.scope?.userStoryId ?? null,
                                            }
                                        }
                                        : {}),
                                    ...(candidate.confirmationToken ? { confirmationToken: candidate.confirmationToken } : {})
                                };
                            })
                            .filter((candidate): candidate is WorkProductConfirmationCandidate => candidate !== null)
                        : [];

                    const topCandidate = mappedCandidates[0];
                    const backendOnConfirmArgs = payload.onConfirm?.arguments;
                    const replayArgumentsFromTemplate: AppChatEvents["generate_work_product"] | undefined =
                        payload.onConfirmTemplate?.tool === "generate_work_product" && topCandidate
                            ? {
                                workProductId: topCandidate.workProduct.id,
                                workProductName: topCandidate.workProduct.name,
                                ...(topCandidate.scope ? { scope: topCandidate.scope } : {}),
                                ...(typeof payload.onConfirmTemplate?.arguments?.forceNewVersion === "boolean"
                                    ? { forceNewVersion: payload.onConfirmTemplate.arguments.forceNewVersion }
                                    : {}),
                                ...(backendConfirmationToken || topCandidate.confirmationToken
                                    ? { confirmationToken: backendConfirmationToken || topCandidate.confirmationToken }
                                    : {})
                            }
                            : undefined;
                    const replayArgumentsFromLegacyOnConfirm: AppChatEvents["generate_work_product"] | undefined = backendOnConfirmArgs
                        ? {
                            ...backendOnConfirmArgs,
                            ...(backendConfirmationToken ? { confirmationToken: backendConfirmationToken } : {})
                        }
                        : undefined;
                    const replayArguments = replayArgumentsFromTemplate || replayArgumentsFromLegacyOnConfirm;
                    const confirmationPrompt = mappedCandidates.length > 1
                        ? `${payload.message || "I found a few close matches."} Reply with ${mappedCandidates.map(candidate => candidate.option).join(" or ")} to choose: ${mappedCandidates.map(candidate => `${candidate.option}) ${candidate.workProduct.name}`).join("; ")}.`
                        : payload.message || "Please confirm this generation request.";

                    return {
                        ok: false as const,
                        requestId: payload.requestId,
                        error: payload.message || "Confirmation required.",
                        confirmationRequired: true,
                        ...(backendConfirmationToken ? { confirmationToken: backendConfirmationToken } : {}),
                        confirmationPrompt,
                        ...(mappedCandidates.length > 0 ? { candidates: mappedCandidates } : {}),
                        closestMatch: payload.closestMatch?.workProductId && payload.closestMatch?.workProductName
                            ? { id: payload.closestMatch.workProductId, name: payload.closestMatch.workProductName }
                            : undefined,
                        ...(payload.onConfirmTemplate?.tool === "generate_work_product"
                            ? { onConfirmTemplate: payload.onConfirmTemplate }
                            : {}),
                        ...(replayArguments
                            ? {
                                onConfirm: {
                                    tool: "generate_work_product" as const,
                                    arguments: replayArguments
                                }
                            }
                            : {})
                    };
                }

                if (payload.status === "not_available") {
                    return {
                        ok: false as const,
                        requestId: payload.requestId,
                        error: payload.message || `No Work Product matched "${request.workProductName || request.workProductId}" in the current hierarchy.`,
                        availableWorkProducts: payload.availableWorkProducts || []
                    };
                }

                return null;
            } catch (error) {
                console.warn("[AIChatPanel] generate_work_product: resolver unavailable, using local fallback", {
                    error,
                    request,
                    effectiveRequestScope,
                });
                return null;
            }
        },
        [
            currentProjectFromStore,
            normalizePhaseForGeneration,
            mapPhaseToResolverHint,
            normalizeResolverScope,
            resolveCurrentGenerationScope,
            selectedFeature,
            selectedUserStory?.id,
            selectedProject,
            trackResolverGenerationJob,
            workProductsByPhase
        ]
    );

    const resolveWorkProductForGeneration = useCallback(
        (
            request: PendingWorkProductGenerationRequest,
            availableWorkProducts: Record<string, WorkProduct[]> = workProductsByPhase
        ): WorkProduct | null => {
            const phase = normalizePhaseForGeneration(request.phase);
            const preferredPhaseWorkProducts = availableWorkProducts[phase] || [];
            const allPhaseWorkProducts = Object.values(availableWorkProducts).flat();

            // An exact visible title is authoritative across the loaded catalog.
            // Do this before phase-preferred alias/variable resolution, otherwise
            // stale or incorrect catalog metadata in another item can win over the
            // work product the user named (for example User Story Ticket resolving
            // to User Story-to-Spec Mapping).
            const requestedName = normalizeLookupText(request.workProductName);
            if (requestedName) {
                const exactNameMatch = allPhaseWorkProducts.find(
                    workProduct => normalizeLookupText(workProduct.name) === requestedName
                );
                if (exactNameMatch) return exactNameMatch;
            }

            const findInCandidates = (candidates: WorkProduct[]): WorkProduct | null => {
                return findWorkProductGenerationSource(candidates, request) ?? null;
            };

            // Prefer the requested/current phase first, then fall back to all
            // loaded phases so generation still works when the model suggests
            // an incorrect phase hint (e.g. Personas with phase=design).
            return findInCandidates(preferredPhaseWorkProducts) ?? findInCandidates(allPhaseWorkProducts);
        },
        [normalizeLookupText, normalizePhaseForGeneration, workProductsByPhase]
    );

    const refreshWorkProductsForCurrentContext = useCallback(async (): Promise<Record<string, WorkProduct[]> | null> => {
        const effectiveProjectId =
            (currentProjectFromStore as any)?.project_id || (selectedProject as any)?.project_id || (selectedFeature as any)?.projectid;
        if (!effectiveProjectId) return null;

        const featureId = (selectedFeature as any)?.id ?? (selectedFeature as any)?.featureid;
        try {
            if (selectedUserStory?.id && featureId) {
                return await dispatch(
                    fetchWorkProductsByPhase({
                        projectId: Number(effectiveProjectId),
                        featureId: Number(featureId),
                        userStoryId: String(selectedUserStory.id),
                        level: "USERSTORY"
                    })
                ).unwrap();
            }

            if (featureId) {
                return await dispatch(
                    fetchWorkProductsByPhase({
                        projectId: Number(effectiveProjectId),
                        featureId: Number(featureId),
                        level: "FEATURE"
                    })
                ).unwrap();
            }

            return await dispatch(
                fetchWorkProductsByPhase({
                    projectId: Number(effectiveProjectId),
                    level: "PRODUCT"
                })
            ).unwrap();
        } catch (error) {
            console.error("[AIChatPanel] generate_work_product: unable to refresh work products", error);
            return null;
        }
    }, [(currentProjectFromStore as any)?.project_id, dispatch, selectedFeature, (selectedProject as any)?.project_id, selectedUserStory?.id]);

    const resolveRequestedGenerationScopeForTarget = useCallback(
        (
            target: WorkProduct,
            requestedScope?: PendingWorkProductGenerationRequest["scope"]
        ): PendingWorkProductGenerationRequest["scope"] | undefined => {
            if (!requestedScope) return requestedScope;

            const supportedLevels = new Set<"PRODUCT" | "FEATURE" | "USERSTORY">();
            const declaredScope = String(target.scope_level || target.level || "").trim().toUpperCase();
            if (declaredScope === "PRODUCT" || declaredScope === "FEATURE" || declaredScope === "USERSTORY") {
                supportedLevels.add(declaredScope);
            }

            if (Array.isArray(target.display_level)) {
                target.display_level
                    .map(level => String(level || "").trim().toUpperCase())
                    .forEach(level => {
                        if (level === "PRODUCT" || level === "FEATURE" || level === "USERSTORY") {
                            supportedLevels.add(level);
                        }
                    });
            }

            if (supportedLevels.size === 0) return requestedScope;

            const preferredOrder: Array<"PRODUCT" | "FEATURE" | "USERSTORY"> =
                requestedScope.level === "USERSTORY"
                    ? ["USERSTORY", "FEATURE", "PRODUCT"]
                    : requestedScope.level === "FEATURE"
                      ? ["FEATURE", "PRODUCT"]
                      : ["PRODUCT"];

            const resolvedLevel = preferredOrder.find(level => supportedLevels.has(level));
            if (!resolvedLevel) return requestedScope;

            if (resolvedLevel === "PRODUCT") {
                return { level: "PRODUCT", featureId: null, userStoryId: null };
            }

            const featureId =
                requestedScope.featureId || Number((selectedFeature as any)?.id || (selectedFeature as any)?.featureid || 0) || null;

            if (resolvedLevel === "FEATURE") {
                if (!featureId) return requestedScope;
                return { level: "FEATURE", featureId, userStoryId: null };
            }

            const userStoryId = requestedScope.userStoryId || (selectedUserStory?.id ? String(selectedUserStory.id) : null);
            if (!featureId || !userStoryId) {
                return supportedLevels.has("FEATURE") && featureId
                    ? { level: "FEATURE", featureId, userStoryId: null }
                    : requestedScope;
            }

            return { level: "USERSTORY", featureId, userStoryId };
        },
        [selectedFeature, selectedUserStory?.id]
    );

    const tryTriggerWorkProductGeneration = useCallback(
        async (
            request: PendingWorkProductGenerationRequest,
            availableWorkProducts?: Record<string, WorkProduct[]>
        ) => {
            const target = resolveWorkProductForGeneration(request, availableWorkProducts);
            if (!target) return null;

            const effectiveProjectId =
                (currentProjectFromStore as any)?.project_id || (selectedProject as any)?.project_id || (selectedFeature as any)?.projectid;

            const effectiveScope = resolveRequestedGenerationScopeForTarget(target, request.scope);

            const submission = await workProductHandlers.handleWorkProductGenerate(
                target,
                effectiveProjectId ? Number(effectiveProjectId) : undefined,
                { forceNewVersion: !!request.forceNewVersion, scope: effectiveScope }
            );
            if (!submission?.ok) {
                return submission ?? { ok: false as const, error: `The generation job for ${target.name} could not be submitted.` };
            }
            return {
                ok: true as const,
                jobId: submission.jobId,
                workProductId: String(target.id),
                workProductName: target.name,
                message: `Started generating ${target.name}.`
            };
        },
        [
            (currentProjectFromStore as any)?.project_id,
            resolveWorkProductForGeneration,
            resolveRequestedGenerationScopeForTarget,
            selectedFeature,
            (selectedProject as any)?.project_id,
            workProductHandlers
        ]
    );

    const handleGenerateWorkProductRequest = useCallback(
        async (payload: AppChatEvents["generate_work_product"]) => {
            if (payload.elementId) {
                if (currentView !== "features") {
                    toast.error("Open Feature Studio before generating that scoped Optional Work Product.");
                    return { ok: false as const, error: "Open Feature Studio before generating that scoped Optional Work Product." };
                }

                const scopedElement = resolveScopedWorkProductElement(payload.elementId);
                if (!scopedElement) {
                    const parsedElementTarget = extractWorkProductTargetFromElementId(payload.elementId);
                    const fallbackRequest: PendingWorkProductGenerationRequest = {
                        workProductId: payload.workProductId || parsedElementTarget.workProductId,
                        workProductName: payload.workProductName || parsedElementTarget.workProductName,
                        targetFeatureName: payload.targetFeatureName,
                        targetUserStoryName: payload.targetUserStoryName,
                        targetUserStoryNumber: payload.targetUserStoryNumber,
                        phase: normalizePhaseForGeneration(payload.phase || parsedElementTarget.phase),
                        forceNewVersion: payload.forceNewVersion,
                        scope: payload.scope
                    };

                    if (fallbackRequest.workProductId || fallbackRequest.workProductName) {
                        const resolverFallback = await tryResolveAndGenerateViaBackend(fallbackRequest);
                        if (resolverFallback) return resolverFallback;

                        const immediateFallback = await tryTriggerWorkProductGeneration(fallbackRequest);
                        if (immediateFallback) return immediateFallback;

                        const refreshedWorkProducts = await refreshWorkProductsForGenerationRequest(fallbackRequest);
                        if (refreshedWorkProducts) {
                            const refreshedFallback = await tryTriggerWorkProductGeneration(fallbackRequest, refreshedWorkProducts);
                            if (refreshedFallback) return refreshedFallback;
                        }
                    }

                    toast.error("That scoped Work Product is no longer visible. Refresh the available Work Products and try again.");
                    return { ok: false as const, error: "That scoped Work Product is no longer visible. Refresh the available Work Products and try again." };
                }
                const source = Object.values(workProductsByPhase)
                    .flat()
                    .find(workProduct => String(workProduct.id) === scopedElement.workProductId);
                if (!source) {
                    const fallbackRequest: PendingWorkProductGenerationRequest = {
                        workProductId: scopedElement.workProductId,
                        workProductName: scopedElement.workProductName,
                        phase: normalizePhaseForGeneration(payload.phase),
                        forceNewVersion: payload.forceNewVersion,
                        scope: scopedElement.scope
                    };

                    const resolverFallback = await tryResolveAndGenerateViaBackend(fallbackRequest);
                    if (resolverFallback) return resolverFallback;

                    const immediateFallback = await tryTriggerWorkProductGeneration(fallbackRequest);
                    if (immediateFallback) return immediateFallback;

                    const refreshedWorkProducts = await refreshWorkProductsForGenerationRequest(fallbackRequest);
                    if (refreshedWorkProducts) {
                        const refreshedFallback = await tryTriggerWorkProductGeneration(fallbackRequest, refreshedWorkProducts);
                        if (refreshedFallback) return refreshedFallback;
                    }

                    return {
                        ok: false as const,
                        error: "That visible Work Product is not loaded in the current SDD workspace. Refresh and try again."
                    };
                }
                const effectiveProjectId =
                    (currentProjectFromStore as any)?.project_id || (selectedProject as any)?.project_id || (selectedFeature as any)?.projectid;
                const submission = await workProductHandlers.handleWorkProductGenerate(source, Number(effectiveProjectId), {
                    forceNewVersion: !!payload.forceNewVersion,
                    scope: scopedElement.scope
                });
                if (!submission?.ok || !submission.jobId) {
                    return {
                        ok: false as const,
                        error:
                            submission && !submission.ok
                                ? submission.error
                                : `The generation job for ${source.name} could not be submitted.`
                    };
                }
                return {
                    ok: true as const,
                    jobId: submission.jobId,
                    workProductId: String(source.id),
                    workProductName: source.name,
                    message: `Started generating ${source.name}.`
                };
            }

            const request: PendingWorkProductGenerationRequest = {
                workProductId: payload.workProductId,
                workProductName: payload.workProductName,
                targetFeatureName: payload.targetFeatureName,
                targetUserStoryName: payload.targetUserStoryName,
                targetUserStoryNumber: payload.targetUserStoryNumber,
                confirmationToken: payload.confirmationToken,
                phase: normalizePhaseForGeneration(payload.phase),
                forceNewVersion: payload.forceNewVersion,
                scope: payload.scope
            };

            if (currentView !== "features") {
                handleViewChange("features");
            }

            const resolverOutcome = await tryResolveAndGenerateViaBackend(request);
            if (resolverOutcome) return resolverOutcome;

            const immediateResult = await tryTriggerWorkProductGeneration(request);
            if (immediateResult) return immediateResult;

            const refreshedWorkProducts = await refreshWorkProductsForGenerationRequest(request);
            if (refreshedWorkProducts) {
                const refreshedResult = await tryTriggerWorkProductGeneration(request, refreshedWorkProducts);
                if (refreshedResult) return refreshedResult;
            }

            const availableWorkProducts = Object.values(refreshedWorkProducts ?? workProductsByPhase)
                .flat();
            const uniqueWorkProducts = availableWorkProducts.filter(
                (workProduct, index, workProducts) =>
                    Boolean(workProduct.name) && workProducts.findIndex(candidate => String(candidate.id) === String(workProduct.id)) === index
            );
            const availableWorkProductNames = uniqueWorkProducts
                .map(workProduct => workProduct.name)
                .filter((name, index, names) => Boolean(name) && names.indexOf(name) === index);
            const requestedName = payload.workProductName || payload.workProductId || "the requested Work Product";

            if (payload.confirmationToken) {
                const error = "The confirmation request could not be completed because the backend resolver is unavailable. Please try again.";
                toast.error(error);
                return { ok: false as const, error, availableWorkProducts: availableWorkProductNames };
            }

            if (payload.workProductName) {
                const closestMatch = findClosestWorkProductGenerationSource(uniqueWorkProducts, payload.workProductName);
                if (closestMatch) {
                    const confirmationPrompt = `Did you mean "${closestMatch.name}"? Would you like me to generate it?`;
                    return {
                        ok: false as const,
                        error: `No Work Product exactly matched "${payload.workProductName}". Ask the user to confirm the closest match before generating.`,
                        confirmationRequired: true,
                        confirmationPrompt,
                        closestMatch: { id: String(closestMatch.id), name: closestMatch.name },
                        onConfirm: {
                            tool: "generate_work_product" as const,
                            arguments: {
                                workProductId: String(closestMatch.id),
                                workProductName: closestMatch.name,
                                phase: closestMatch.phase || payload.phase,
                                forceNewVersion: payload.forceNewVersion
                            }
                        },
                        availableWorkProducts: availableWorkProductNames
                    };
                }
            }

            const error = `No Work Product matched "${requestedName}" in the current hierarchy.`;
            toast.error(error);
            return { ok: false as const, error, availableWorkProducts: availableWorkProductNames };
        },
        [
            currentView,
            currentProjectFromStore,
            extractWorkProductTargetFromElementId,
            handleViewChange,
            normalizePhaseForGeneration,
            refreshWorkProductsForGenerationRequest,
            tryTriggerWorkProductGeneration,
            tryResolveAndGenerateViaBackend,
            workProductsByPhase,
            selectedFeature,
            selectedProject,
            workProductHandlers
        ]
    );

    const featureHandlers = useMemo(
        () =>
            createFeatureHandlers(
                initiatives,
                selectedProject?.id || null,
                selectedFeature,
                setInitiatives,
                setSelectedFeature,
                setLastUsedMatrixId,
                setFeatureCreationProgress,
                setShowAddFeatureModal,
                setFeatureInfoData,
                setShowEditWarning,
                setIsFeatureInfoEditing,
                setImpactedWorkProducts,
                setShowWorkProductsImpactModal,
                getFeatureMockData
            ),
        [initiatives, selectedProject?.id, selectedFeature]
    );

    const projectHandlers = useMemo(() => {
        const baseHandlers = createProjectHandlers(
            setInitiatives,
            (projectId: string) => {
                const project = initiatives.find(init => init.id === projectId);
                if (project) {
                    handleProjectSelect(project);
                }
            },
            setSelectedFeature,
            setLastUsedMatrixId,
            handleViewChange,
            initiatives,
            token
        );

        // Enhanced handlers with validation
        return {
            ...baseHandlers,
            handleCreateProject: (projectName: string, projectData?: Partial<Initiative>) => {
                const newProjectData = { name: projectName, ...projectData };

                // Validate before creating
                if (validateEntityData("project", newProjectData, initiatives)) {
                    baseHandlers.handleCreateProject(projectName);
                }
            },
            handleSelectProject: handleProjectSelect,
            handleBackToProjects: () => {
                setSelectedProject(null);
                setSelectedFeature(null);
                setSelectedUserStory(null);
                setSelectedWorkProduct(null);
                // Leaving the project entirely: don't let a later editor
                // restore resurrect a work product from this session.
                lastKnownWorkProductRef.current = null;
                handleViewChange("projects");
            },
            handleUpdateProject: (projectId: string, updatedData: Partial<Initiative>) => {
                setInitiatives(prev => prev.map(init => (init.id === projectId ? { ...init, ...updatedData } : init)));

                // Update selected project if it's the one being updated
                if (selectedProject?.id === projectId) {
                    setSelectedProject(prev => (prev ? { ...prev, ...updatedData } : null));
                }
            }
        };
    }, [handleViewChange, initiatives, handleProjectSelect, validateEntityData, selectedProject, token]);

    const chatHandlers = useMemo(
        () =>
            createChatHandlers(
                chatStep,
                setChatStep,
                setChatMessages,
                setCurrentChatInput,
                setInitiativeData,
                setShowThemeHelp,
                fileInputRef,
                initiativeData,
                STRATEGIC_THEMES
            ),
        [chatStep, initiativeData]
    );

    // Simplified and optimized auto-load effect with debouncing
    // DISABLED: No longer auto-select features - show Product/Project level by default
    // useEffect(() => {
    //     // This auto-selection logic has been disabled to show Product/Project level WPs by default
    //     // Features must now be manually selected by the user from the hierarchy
    // }, [initiatives.length, selectedProject, selectedFeature, firstFeatureInProject, lastUsedMatrixId]);

    // Auto-scroll chat to bottom when new messages arrive (optimized)
    useEffect(() => {
        if (chatScrollRef.current && chatMessages.length > 0) {
            requestAnimationFrame(() => {
                if (chatScrollRef.current) {
                    chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
                }
            });
        }
    }, [chatMessages.length]); // Only depend on length, not full array

    // Loading progress effect (unchanged)
    useEffect(() => {
        if (isLoadingNewInitiative) {
            const interval = setInterval(() => {
                setLoadingProgress(prev => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        return 100;
                    }
                    return prev + 20;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isLoadingNewInitiative]);

    // Jira Sync feedback (legacy JiraSyncModal was removed — see Task 16; this
    // handler is retained for the snackbar plumbing further down).
    const handleJiraSyncComplete = useCallback((success: boolean, message: string) => {
        setJiraSyncFeedback({
            show: true,
            message,
            severity: success ? "success" : "error"
        });
    }, []);

    // Feature editing handlers (memoized)
    const handleEditWarningConfirm = useCallback(() => {
        setShowEditWarning(false);
        setIsFeatureInfoEditing(true);
    }, []);

    const handleFeatureInfoCancel = useCallback(() => {
        setIsFeatureInfoEditing(false);
        setFeatureInfoData({ title: "", description: "", goals: [], successMetrics: [], labels: [] });
    }, []);

    const handleWorkProductsImpactConfirm = useCallback(() => {
        if (selectedFeature) {
            // Validate feature data before updating
            const featureData = { name: featureInfoData.title, labels: featureInfoData.labels };
            const allFeatures = initiatives.flatMap(init => init.features);

            if (validateEntityData("feature", featureData, allFeatures)) {
                setInitiatives(prev =>
                    prev.map(init => ({
                        ...init,
                        features: init.features.map(feature =>
                            feature.id === selectedFeature.id ? { ...feature, name: featureInfoData.title, labels: featureInfoData.labels } : feature
                        )
                    }))
                );
                setSelectedFeature(prev =>
                    prev
                        ? {
                              ...prev,
                              name: featureInfoData.title,
                              labels: featureInfoData.labels
                          }
                        : null
                );
            }
        }
        setShowWorkProductsImpactModal(false);
        setIsFeatureInfoEditing(false);
        setFeatureInfoData({ title: "", description: "", goals: [], successMetrics: [], labels: [] });
    }, [selectedFeature, featureInfoData, initiatives, validateEntityData]);

    // Feature promotion handler (memoized)
    const handleFeaturePromotion = useCallback(
        (featureData: any) => {
            // Create a new project from the promoted feature
            const newProjectName = featureData.projectType || "New Project";

            // Validate before promoting
            if (validateEntityData("project", { name: newProjectName }, initiatives)) {
                projectHandlers.handleCreateProject(newProjectName);

                // Navigate back to projects view to show the new project
                setShowContextMeshReturn(false);
                setCurrentView("projects");

                console.log("Feature promoted to project:", {
                    newProjectName,
                    originalFeature: selectedFeature?.name,
                    setupData: featureData
                });
            }
        },
        [projectHandlers, selectedFeature?.name, initiatives, validateEntityData]
    );

    // File input handler (memoized)
    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files || []);
            setInitiativeData(prev => ({ ...prev, files }));

            if (files.length > 0) {
                const fileNames = files.map(f => f.name).join(", ");
                const aiMessage: ChatMessage = {
                    id: generateId(),
                    type: "ai",
                    content: `📁 **Files attached:** ${fileNames}\n\n${chatHandlers.generateFinalSummary()}`,
                    timestamp: new Date()
                };
                setChatMessages(prev => [...prev, aiMessage]);
                setChatStep(5);
            }
        },
        [chatHandlers]
    );

    // Refresh projects handler (memoized)
    const handleRefreshProjects = useCallback(() => {
        if (token) {
            // Refresh projects from the backend
            dispatch(fetchProjects());
        }
    }, [token, dispatch]);

    const isProductScopedView = isProductScopedChatView(currentView);
    const persistedProductForChat = isProductScopedView ? (persistedChatScopeRef.current?.product ?? null) : null;
    const liveProductForChat = isProductScopedView
        ? resolveActiveProductContext(currentProjectFromStore, selectedProject)
        : null;
    const activeProductForChat = liveProductForChat?.projectId != null
        ? liveProductForChat
        : persistedProductForChat ?? liveProductForChat;
    const activeHierarchyForChat: CurrentHierarchyContext | null = (() => {
        if (!isProductScopedView) return null;

        const projectId = activeProductForChat?.projectId ?? null;
        const projectName = activeProductForChat?.projectName ?? null;
        const featureId =
            (selectedFeature?.featureid as string | number | undefined) ??
            (selectedFeature?.id as string | number | undefined) ??
            null;
        const featureName = (selectedFeature?.title ?? selectedFeature?.name ?? null) as string | null;
        const userStoryId = selectedUserStory?.id ? String(selectedUserStory.id) : null;
        const userStoryName = (selectedUserStory?.title ?? selectedUserStory?.name ?? null) as string | null;

        if (selectedUserStory && userStoryName) {
            return {
                hierarchyLevel: "user_story",
                hierarchyName: userStoryName,
                projectId,
                projectName,
                featureId,
                featureName,
                userStoryId,
                userStoryName
            };
        }

        if (selectedFeature && featureName) {
            return {
                hierarchyLevel: "feature",
                hierarchyName: featureName,
                projectId,
                projectName,
                featureId,
                featureName,
                userStoryId: null,
                userStoryName: null
            };
        }

        if (projectName) {
            return {
                hierarchyLevel: "product",
                hierarchyName: projectName,
                projectId,
                projectName,
                featureId: null,
                featureName: null,
                userStoryId: null,
                userStoryName: null
            };
        }

        return null;
    })();

    const effectiveWorkProductForChat = selectedWorkProduct
        ? {
              workProductId: selectedWorkProduct.id ?? null,
              workProductTitle: selectedWorkProduct.name ?? null,
              workProductLevel: selectedWorkProduct.level ?? (selectedUserStory ? "USER_STORY" : selectedFeature ? "FEATURE" : "PRODUCT"),
              workProductContentType: selectedWorkProduct.content_type ?? null,
              workProductPhase: selectedWorkProduct.phase ?? null,
              editorSurface,
              featureId:
                  (selectedFeature?.featureid as string | number | undefined) ??
                  (selectedFeature?.id as string | number | undefined) ??
                  null,
              featureName: (selectedFeature as any)?.title ?? (selectedFeature as any)?.name ?? null,
              userStoryId: selectedUserStory?.id ? String(selectedUserStory.id) : null,
              userStoryTitle: selectedUserStory?.title ?? selectedUserStory?.name ?? null
          }
        : null;

    const activeWorkProductForChat = deriveActiveWorkProductForChat(currentView, effectiveWorkProductForChat);

    useEffect(() => {
        const nextScope: PersistedChatScope = {
            view: VALID_VIEWS.has(currentView) ? currentView : "projects",
            product: activeProductForChat ?? null,
            workProduct: activeWorkProductForChat ?? null
        };
        persistedChatScopeRef.current = nextScope;
        persistChatScope(nextScope);
    }, [
        currentView,
        activeProductForChat?.projectId,
        activeProductForChat?.projectName,
        activeWorkProductForChat?.workProductId,
        activeWorkProductForChat?.workProductTitle,
        activeWorkProductForChat?.workProductLevel,
        activeWorkProductForChat?.workProductContentType,
        activeWorkProductForChat?.workProductPhase,
        activeWorkProductForChat?.editorSurface,
        activeWorkProductForChat?.featureId,
        activeWorkProductForChat?.featureName,
        activeWorkProductForChat?.userStoryId,
        activeWorkProductForChat?.userStoryTitle
    ]);

    return (
        <ThemeProvider theme={muiTheme}>
            <CssBaseline />

            {/* Global Progress Indicators */}
            {globalProgress && (
                <LinearProgressIndicator
                    placement="global"
                    variant={globalProgress.currentStep ? "determinate" : "indeterminate"}
                    value={globalProgress.currentStep && globalProgress.totalSteps ? (globalProgress.currentStep / globalProgress.totalSteps) * 100 : undefined}
                    showStatusText={true}
                    step={globalProgress.step}
                    currentStep={globalProgress.currentStep}
                    totalSteps={globalProgress.totalSteps}
                    statusText={globalProgress.message}
                    duration={globalProgress.duration}
                    onCancel={globalProgress.onCancel}
                    {...globalProgress}
                />
            )}

            {/* Feature Creation Progress */}
            {featureCreationProgress && (
                <LinearProgressIndicator
                    placement="local"
                    variant={featureCreationProgress.currentStep ? "determinate" : "indeterminate"}
                    value={
                        featureCreationProgress.currentStep && featureCreationProgress.totalSteps
                            ? (featureCreationProgress.currentStep / featureCreationProgress.totalSteps) * 100
                            : undefined
                    }
                    showStatusText={true}
                    step={featureCreationProgress.step}
                    currentStep={featureCreationProgress.currentStep}
                    totalSteps={featureCreationProgress.totalSteps}
                    duration={featureCreationProgress.duration}
                    {...featureCreationProgress}
                />
            )}

            {/* Validation Errors Global Alert */}
            {Object.keys(validationErrors).length > 0 && (
                <MuiAlert
                    severity="error"
                    sx={{
                        position: "fixed",
                        top: 16,
                        right: 16,
                        zIndex: 1400,
                        maxWidth: 400
                    }}
                    onClose={() => setValidationErrors({})}
                >
                    <MuiTypography variant="subtitle2" gutterBottom>
                        Validation Issues Found:
                    </MuiTypography>
                    {Object.entries(validationErrors).map(([entityType, errors]) => (
                        <MuiBox key={entityType} sx={{ mb: 1 }}>
                            <MuiTypography variant="body2" sx={{ fontWeight: 500 }}>
                                {PLATFORM_TERMINOLOGY.entities[entityType as keyof typeof PLATFORM_TERMINOLOGY.entities]}:
                            </MuiTypography>
                            <MuiBox component="ul" sx={{ m: 0, pl: 2 }}>
                                {errors.map((error, index) => (
                                    <li key={index}>
                                        <MuiTypography variant="caption">{error}</MuiTypography>
                                    </li>
                                ))}
                            </MuiBox>
                        </MuiBox>
                    ))}
                </MuiAlert>
            )}

            {/* Toast Notifications */}
            <Toaster
                position="top-right"
                expand={true}
                richColors={true}
                closeButton={true}
                toastOptions={{
                    style: {
                        background: "#ffffff",
                        color: "#212121",
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        fontSize: "14px"
                    },
                    success: {
                        style: {
                            background: "#4CAF50",
                            color: "#ffffff"
                        }
                    },
                    error: {
                        style: {
                            background: "#AC1739",
                            color: "#ffffff"
                        }
                    }
                }}
            />

            <MuiBox
                sx={{
                    display: "flex",
                    height: "100vh",
                    bgcolor: "background.default",
                    overflow: "hidden"
                }}
            >
                {/* Navigation Rail */}
                <Navigation
                    currentView={currentView}
                    onViewChange={view => {
                        if (view === "context-mesh-console") {
                            setStandaloneContextMeshId(null);
                            setStandaloneContextMeshSection("console");
                        }
                        handleViewChange(view);
                    }}
                    hasProductStudio={hasProductStudio}
                    canViewContextMesh={canViewContextMeshConsole}
                    canManageFiles={canManageContextMeshFiles}
                    canManageIntegrations={canManageContextMeshIntegrations}
                />

                {/* Main Content Container - Adjusted width when AI Chat is right-docked */}
                <MuiBox
                    sx={{
                        flex: 1,
                        overflow: "hidden",
                        transition: "max-width 0.15s ease",
                        maxWidth: `calc(100vw - ${NAV_RAIL_WIDTH + chatWidth}px)`
                    }}
                >
                    {currentView === "projects" && productsDashboardV2Enabled && (
                        <ProductsDashboard
                            token={token}
                            refreshSignal={productsRefreshSignal}
                            asyncProductCreationEnabled={durableProductCreationEnabled}
                            idempotencyUserScope={userId}
                            initialCreationOperation={
                                productCreationOperationSeed && productCreationOperationSeed.userId === userId
                                    ? productCreationOperationSeed.operation
                                    : null
                            }
                            onInitialCreationOperationConsumed={() => setProductCreationOperationSeed(null)}
                            onCreationProjectLinked={(projectId) => {
                                void dispatch(setCurrentProject({ projectId, refreshProjects: false }))
                                    .unwrap()
                                    .catch(error => console.error("Failed to select the asynchronously created product:", error));
                            }}
                            onOpenProduct={async (productId) => {
                                sessionStorage.setItem("hasVisitedProjects", "true");
                                const project = initiatives.find((init) => init.id === productId);
                                if (!project) return;
                                if (token) {
                                    try {
                                        await setCurrentProjectApi(parseInt(productId, 10), token);
                                    } catch (error) {
                                        console.error("Failed to set project as active:", error);
                                    }
                                }
                                projectHandlers.handleSelectProject(project);
                            }}
                            onCreateProduct={() => {
                                setNewProductDraftId(null);
                                setNewProductInitialKind(null);
                                setNewProductInitialPatch(null);
                                handleViewChange("new-product");
                            }}
                            onResumeDraft={(draftId) => {
                                setNewProductDraftId(draftId);
                                setNewProductInitialKind(null);
                                setNewProductInitialPatch(null);
                                handleViewChange("new-product");
                            }}
                            onDeleteProduct={async (productId) => {
                                await projectHandlers.handleDeleteProject(productId);
                                setProductsRefreshSignal((n) => n + 1);
                            }}
                            onEditProduct={(productId) => {
                                setRefreshProductId(productId);
                                handleViewChange("refresh-product", { skipNewProductGuard: true });
                            }}
                            onNavigateToIntegrations={() => handleViewChange("integrations")}
                            onOpenProcessingJob={openBackgroundActivityForJob}
                            requestedBuildProductId={requestedBuildProductId}
                            onBuildRequestHandled={() => setRequestedBuildProductId(null)}
                            onBuildStateChange={handleRefreshProjects}
                        />
                    )}
                    {hasContextMesh && canViewContextMeshConsole && (
                        <MuiBox sx={{ display: currentView === "context-mesh-console" ? "block" : "none", height: "100%" }} aria-hidden={currentView !== "context-mesh-console"}>
                            {standaloneContextMeshId ? (
                                <MeshStrengthView
                                        projectId={null}
                                        contextMeshId={standaloneContextMeshId}
                                        contextMeshTitle={standaloneContextMeshTitle}
                                        canRefreshScore={standaloneContextMeshAccess?.contextMeshId === standaloneContextMeshId
                                            && (standaloneContextMeshAccess.accessRole === "owner" || standaloneContextMeshAccess.accessRole === "editor")}
                                        refreshSignal={workspaceContextMeshAnalysisRefreshSignal}
                                        onNavigate={view => handleViewChange(view, { skipNewProductGuard: true })}
                                        onBackToFeatureStudio={() => {
                                            setStandaloneContextMeshId(null);
                                            setStandaloneContextMeshSection("console");
                                            if (typeof window !== "undefined") {
                                                window.history.pushState({ view: "context-mesh-console" }, "", CONTEXT_MESH_HUB_ROUTE);
                                            }
                                        }}
                                        backLabel="Back to Context Mesh Hub"
                                        onOpenWorkspaceRefresh={canCreateStandaloneContextMesh ? () => {
                                            setContextMeshRefreshReturnTarget("mesh");
                                            handleViewChange("refresh-context-mesh", {
                                                skipNewProductGuard: true,
                                                route: contextMeshRefreshUrl(standaloneContextMeshId)
                                            });
                                        } : undefined}
                                        onOpenWorkspaceFiles={canManageContextMeshFiles ? () => {
                                            setSharedContextMeshTargetId(standaloneContextMeshId);
                                            setStandaloneContextMeshSection("files");
                                            if (typeof window !== "undefined") {
                                                window.history.pushState(
                                                    { view: "context-mesh-console", contextMeshId: standaloneContextMeshId, section: "files" },
                                                    "",
                                                    contextMeshHubUrl(standaloneContextMeshId, "files")
                                                );
                                            }
                                        } : undefined}
                                        onOpenWorkspaceIntegrations={canManageContextMeshIntegrations ? () => {
                                            setSharedContextMeshTargetId(standaloneContextMeshId);
                                            setStandaloneContextMeshSection("integrations");
                                            if (typeof window !== "undefined") {
                                                window.history.pushState(
                                                    { view: "context-mesh-console", contextMeshId: standaloneContextMeshId, section: "integrations" },
                                                    "",
                                                    contextMeshHubUrl(standaloneContextMeshId, "integrations")
                                                );
                                            }
                                        } : undefined}
                                    />
                            ) : (
                                <ContextMeshConsole
                                    token={token}
                                    canCreateMesh={canCreateStandaloneContextMesh}
                                    canViewAnalysis={canViewContextMeshAnalysis}
                                    refreshSignal={contextMeshHubRefreshSignal}
                                    highlightedContextMeshId={newlyCreatedContextMeshId}
                                    onCreateContextMesh={() => {
                                        setStandaloneContextMeshDraftId(null);
                                        handleViewChange("new-context-mesh", { skipNewProductGuard: true });
                                    }}
                                    onResumeContextMeshDraft={draft => {
                                        setStandaloneContextMeshDraftId(draft.draft_id);
                                        handleViewChange("new-context-mesh", { skipNewProductGuard: true });
                                        if (typeof window !== "undefined") {
                                            window.history.replaceState(
                                                { view: "new-context-mesh", draftId: draft.draft_id },
                                                "",
                                                contextMeshDraftUrl(draft.draft_id)
                                            );
                                        }
                                    }}
                                    onOpenContextMesh={contextMesh => {
                                        setStandaloneContextMeshId(contextMesh.contextMeshId);
                                        setStandaloneContextMeshTitle(contextMesh.name);
                                        setStandaloneContextMeshSection("console");
                                        setNewlyCreatedContextMeshId(null);
                                        if (typeof window !== "undefined") {
                                            window.history.pushState(
                                                { view: "context-mesh-console", contextMeshId: contextMesh.contextMeshId },
                                                "",
                                                `${CONTEXT_MESH_HUB_ROUTE}/${contextMesh.contextMeshId}`
                                            );
                                        }
                                    }}
                                    onEditContextMesh={canCreateStandaloneContextMesh ? contextMesh => {
                                        setStandaloneContextMeshId(contextMesh.contextMeshId);
                                        setStandaloneContextMeshTitle(contextMesh.name);
                                        setStandaloneContextMeshSection("console");
                                        setNewlyCreatedContextMeshId(null);
                                        setContextMeshRefreshReturnTarget("hub");
                                        handleViewChange("refresh-context-mesh", {
                                            skipNewProductGuard: true,
                                            route: contextMeshRefreshUrl(contextMesh.contextMeshId)
                                        });
                                    } : undefined}
                                />
                            )}
                        </MuiBox>
                    )}
                    {currentView === "new-context-mesh" && canCreateStandaloneContextMesh && (
                        <StandaloneContextMeshCreationScreen
                            token={token}
                            draftId={standaloneContextMeshDraftId}
                            draftRef={newProductDraftRef}
                            formRef={newProductFormRef}
                            onDirtyChange={dirty => { standaloneContextMeshDirtyRef.current = dirty; }}
                            onDraftIdentityChange={draftId => {
                                setStandaloneContextMeshDraftId(current => current === draftId ? current : draftId);
                                if (typeof window !== "undefined" && window.location.pathname !== contextMeshDraftUrl(draftId)) {
                                    window.history.replaceState(
                                        { view: "new-context-mesh", draftId },
                                        "",
                                        contextMeshDraftUrl(draftId)
                                    );
                                }
                            }}
                            onCancel={() => {
                                setStandaloneContextMeshDraftId(null);
                                handleViewChange("context-mesh-console", { skipNewProductGuard: true });
                            }}
                            onCreated={contextMeshId => {
                                setStandaloneContextMeshDraftId(null);
                                setNewlyCreatedContextMeshId(contextMeshId);
                                setContextMeshHubRefreshSignal(value => value + 1);
                                setCurrentView("context-mesh-console");
                                if (typeof window !== "undefined") {
                                    window.history.replaceState(
                                        { view: "context-mesh-console", contextMeshId },
                                        "",
                                        CONTEXT_MESH_HUB_ROUTE
                                    );
                                }
                            }}
                        />
                    )}
                    {currentView === "refresh-context-mesh" && canCreateStandaloneContextMesh && standaloneContextMeshId && (
                        <StandaloneContextMeshRefreshScreen
                            token={token}
                            contextMeshId={standaloneContextMeshId}
                            onCancel={() => {
                                const returnToHub = contextMeshRefreshReturnTarget === "hub";
                                if (returnToHub) {
                                    setStandaloneContextMeshId(null);
                                    setStandaloneContextMeshSection("console");
                                }
                                handleViewChange("context-mesh-console", {
                                    skipNewProductGuard: true,
                                    route: returnToHub
                                        ? CONTEXT_MESH_HUB_ROUTE
                                        : contextMeshHubUrl(standaloneContextMeshId)
                                });
                            }}
                            onRefreshed={contextMeshId => {
                                const returnToHub = contextMeshRefreshReturnTarget === "hub";
                                setWorkspaceContextMeshAnalysisRefreshSignal(value => value + 1);
                                setContextMeshHubRefreshSignal(value => value + 1);
                                if (returnToHub) {
                                    setStandaloneContextMeshId(null);
                                    setStandaloneContextMeshSection("console");
                                }
                                handleViewChange("context-mesh-console", {
                                    skipNewProductGuard: true,
                                    route: returnToHub
                                        ? CONTEXT_MESH_HUB_ROUTE
                                        : contextMeshHubUrl(contextMeshId)
                                });
                            }}
                        />
                    )}
                    {currentView === "projects" && !productsDashboardV2Enabled && (
                        <ProjectsHub
                            onViewChange={handleViewChange}
                            initiatives={initiatives}
                            onCreateProject={projectHandlers.handleCreateProject}
                            selectedProject={selectedProject} // Pass full object
                            onSelectProject={projectHandlers.handleSelectProject}
                            onDeleteProject={projectHandlers.handleDeleteProject}
                            onUpdateProject={projectHandlers.handleUpdateProject}
                            onRefreshProjects={handleRefreshProjects}
                            token={token}
                            isLoadingProjects={projectsStatus === "loading"}
                            onEditProduct={(productId) => {
                                setRefreshProductId(productId);
                                handleViewChange("refresh-product", { skipNewProductGuard: true });
                            }}
                        />
                    )}
                    {currentView === "new-product" && (
                        <NewProductScreen
                            key={newProductDraftId ?? "new"}
                            ref={newProductFormRef}
                            draftRef={newProductDraftRef}
                            draftId={newProductDraftId}
                            initialKind={newProductInitialKind}
                            initialDraftPatch={newProductInitialPatch}
                            asyncProductCreationEnabled={durableProductCreationEnabled}
                            idempotencyUserScope={userId}
                            onCancel={() => {
                                handleViewChange(hasProductStudio ? "projects" : "context-mesh-console", { skipNewProductGuard: true });
                            }}
                            onCreated={(productId, creationOperation) => {
                                if (creationOperation && userId) setProductCreationOperationSeed({ userId, operation: creationOperation });
                                completeProductCreationNavigation({
                                    navigateToProductHub: () => {
                                        handleViewChange(hasProductStudio ? "projects" : "context-mesh-console", { skipNewProductGuard: true });
                                    },
                                    refreshProducts: () => {
                                        if (productsDashboardV2Enabled) {
                                            setProductsRefreshSignal(n => n + 1);
                                        } else {
                                            handleRefreshProjects();
                                        }
                                    },
                                    selectCurrentProject: productId == null
                                        ? undefined
                                        : () => dispatch(setCurrentProject({
                                              projectId: productId,
                                              refreshProjects: false
                                          })).unwrap(),
                                    onSelectionError: error => {
                                        // The Product exists and Product Hub is already open;
                                        // current-product persistence can recover independently.
                                        console.error("Failed to select the newly created product:", error);
                                    }
                                });
                            }}
                            intakeFiles={newProductIntakeFiles}
                            onIntakeConsumed={consumeNewProductIntake}
                        />
                    )}
                    {currentView === "refresh-product" && refreshProductId != null && (
                        <NewProductScreen
                            key={`refresh-${refreshProductId}`}
                            mode="refresh"
                            refreshProductId={refreshProductId}
                            onCancel={() => handleViewChange("projects", { skipNewProductGuard: true })}
                            // onCreated is a create-mode-only callback (unused when mode="refresh",
                            // which resolves via onRefreshComplete instead) but the prop is required
                            // by NewProductScreenProps, so a no-op satisfies the type.
                            onCreated={() => {}}
                            onRefreshComplete={(outcome) => {
                                setProductsRefreshSignal(n => n + 1);
                                handleRefreshProjects();
                                handleViewChange(outcome.kind === "rebuilt" ? "features" : "projects", { skipNewProductGuard: true });
                            }}
                            onBackToContextMesh={
                                showContextMeshReturn
                                    ? () => handleViewChange("mesh-strength", { skipNewProductGuard: true })
                                    : undefined
                            }
                        />
                    )}
                    {currentView === "mesh-strength" && (
                        <MeshStrengthView
                            projectId={currentProjectFromStore?.project_id ?? selectedProject?.project_id ?? null}
                            refreshSignal={productContextMeshAnalysisRefreshSignal}
                            onNavigate={view => handleViewChange(view, { skipNewProductGuard: true })}
                            onOpenRefresh={pid => {
                                setRefreshProductId(pid);
                                handleViewChange("refresh-product", { skipNewProductGuard: true });
                            }}
                            onBackToFeatureStudio={() => handleViewChange(hasProductStudio ? "features" : "context-mesh-console", { skipNewProductGuard: true })}
                            backLabel={hasProductStudio ? "Back to Feature Studio" : "Back to Context Mesh Console"}
                        />
                    )}
                    {currentView === "features" && (
                        <FeatureStudioView
                            key={selectedProject?.project_id ?? selectedProject?.id ?? "no-project"}
                            isMobile={isMobile}
                            selectedProject={selectedProject} // Pass full object instead of ID
                            initiatives={initiatives}
                            selectedFeature={selectedFeature}
                            selectedUserStory={selectedUserStory}
                            isHierarchyCollapsed={isHierarchyCollapsed}
                            featureStudioTab={featureStudioTab}
                            expandedAccordions={expandedAccordions}
                            workProductsByPhase={workProductsByPhase}
                            expandedWorkProducts={expandedWorkProducts}
                            generatingWorkProducts={generatingWorkProducts}
                            processingWorkProducts={processingWorkProducts}
                            workProductProgress={workProductProgress}
                            isFeatureInfoEditing={isFeatureInfoEditing}
                            featureInfoData={featureInfoData}
                            featuresRefreshTrigger={featuresRefreshTrigger}
                            workProductRefreshTrigger={workProductRefreshTrigger}
                            workProductRefreshScope={workProductRefreshScope}
                            hierarchyExpandedItems={hierarchyExpandedItems}
                            onHierarchyExpandedItemsChange={setHierarchyExpandedItems}
                            onBackToProjects={projectHandlers.handleBackToProjects}
                            onAddFeature={() => setShowAddFeatureModal(true)}
                            onAddUserStory={() => setShowAddUserStoryModal(true)}
                            onCreateFromHierarchy={handleCreateFromHierarchy}
                            onHierarchyToggle={handleHierarchyToggle}
                            onFeatureSelect={handleFeatureSelect}
                            onUserStorySelect={setSelectedUserStory}
                            optionalWorkProductScope={optionalWorkProductScope}
                            onOptionalWorkProductScopeChange={setOptionalWorkProductScope}
                            onAccordionToggle={handleAccordionToggle}
                            onFeatureInfoEdit={featureHandlers.handleFeatureInfoEdit}
                            onGenerateWithAI={featureHandlers.handleGenerateWithAI}
                            onFeatureInfoData={setFeatureInfoData}
                            handleWorkProductSelect={workProductHandlers.handleWorkProductSelect}
                            handleWorkProductGenerate={workProductHandlers.handleWorkProductGenerate}
                            handleAddWorkProductToEditable={workProductHandlers.handleAddWorkProductToEditable}
                            handleToggleExpanded={workProductHandlers.handleToggleExpanded}
                            handleApprovalToggle={workProductHandlers.handleApprovalToggle}
                            handleCancelWorkProductProgress={workProductHandlers.handleCancelWorkProductProgress}
                            onFeaturePromote={handleFeaturePromotion}
                            onEditFeature={handleEditFeature}
                            onDeleteFeature={handleDeleteFeature}
                            onEditUserStory={handleEditUserStory}
                            onDeleteUserStory={handleDeleteUserStory}
                            deletingFeatureId={deletingFeatureId}
                            onFeatureDeleteComplete={() => setDeletingFeatureId(null)}
                            onNavigateToIntegrations={() => handleViewChange("integrations")}
                            onOpenMeshStrength={() => handleViewChange("mesh-strength", { skipNewProductGuard: true })}
                        />
                    )}
                    {currentView === "editor" && (
                        <WorkProductEditor
                            workProduct={selectedWorkProduct}
                            onBack={() => {
                                const projectId = resolveNavigationProjectId({
                                    selectedProjectId:
                                        (selectedProject as any)?.project_id ??
                                        selectedProject?.id,
                                    currentProjectId:
                                        (currentProjectFromStore as any)?.project_id ??
                                        editorDeepLink?.projectId,
                                    authProjectId: authDataRef.current.projectId
                                });
                                const navigation =
                                    projectId != null &&
                                    selectedWorkProduct
                                        ? workProductNavigationUrls({
                                              projectId,
                                              workProduct: selectedWorkProduct,
                                              selectedFeature
                                          })
                                        : null;
                                const returnRoute = navigation?.featureStudioUrl ??
                                    (projectId != null
                                        ? featureStudioUrl(projectId)
                                        : undefined);
                                if (
                                    returnRoute &&
                                    typeof window !== "undefined" &&
                                    shouldUseBrowserBackForEditor(
                                        window.history.state,
                                        returnRoute
                                    )
                                ) {
                                    window.history.back();
                                    return;
                                }
                                handleViewChange("features", {
                                    route: returnRoute,
                                    historyMode: "replace"
                                });
                            }}
                            onAIChatToggle={noopChatToggle}
                            showAIChat={true}
                            selectedProject={selectedProject}
                            selectedFeature={selectedFeature}
                            onRebuildStart={handleRebuildStart}
                            onProgressUpdate={handleProgressUpdate}
                            onRebuildJobComplete={handleRebuildComplete}
                            onNavigateToIntegrations={() => handleViewChange("integrations")}
                        />
                    )}
                    {currentView === "uploads" && (
                        <UploadCenter
                            intakeFiles={uploadsIntakeFiles}
                            onIntakeConsumed={consumeUploadsIntake}
                            onAssignmentComplete={handleUploadsAssignmentComplete}
                            refreshSignal={uploadsRefreshSignal}
                            defaultProjectId={routeDefaultProductId}
                            defaultContextMeshId={sharedContextMeshTargetId ?? undefined}
                            onBackToContextMesh={
                                sharedContextMeshTargetId
                                    ? returnToStandaloneContextMesh
                                    : showContextMeshReturn
                                    ? returnToProductContextMesh
                                    : undefined
                            }
                        />
                    )}
                    {currentView === "profile" && <Profile currentView={currentView} onViewChange={handleViewChange} onAIChatToggle={noopChatToggle} />}
                    {currentView === "integrations" && (
                        <Integrations
                            currentView={currentView}
                            onViewChange={handleViewChange}
                            onAIChatToggle={noopChatToggle}
                            refreshSignal={integrationsRefreshSignal}
                            initialTarget={sharedContextMeshTargetId
                                ? { type: "context_mesh", id: sharedContextMeshTargetId }
                                : routeDefaultProductId
                                ? { type: "product", id: String(routeDefaultProductId) }
                                : undefined}
                            onBackToContextMesh={
                                sharedContextMeshTargetId
                                    ? returnToStandaloneContextMesh
                                    : showContextMeshReturn
                                    ? returnToProductContextMesh
                                    : undefined
                            }
                        />
                    )}
                    {currentView === "admin" && <Admin currentView={currentView} onViewChange={handleViewChange} onAIChatToggle={noopChatToggle} />}
                </MuiBox>

                {/* AI Chat Panel — always right-docked, no toggle, no close */}
                <MuiBox
                    sx={{
                        width: chatWidth,
                        height: "100vh",
                        flexShrink: 0,
                        borderLeft: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.paper",
                        zIndex: 1000,
                        position: "relative"
                    }}
                >
                        <AIChatPanel
                            chatActionsRef={chatActionsRef}
                            chatBus={chatBus}
                            navigationAdapter={navigationAdapter}
                            fileIntakeAdapter={fileIntakeAdapter}
                            currentView={currentView}
                            activeContextMeshId={standaloneContextMeshId}
                            activeProduct={activeProductForChat}
                            activeWorkProduct={activeWorkProductForChat}
                            activeHierarchy={activeHierarchyForChat}
                            requestWorkProductGeneration={handleGenerateWorkProductRequest}
                            openFeatureStudioEntityRef={openFeatureStudioEntityRef}
                            suppressFeatureStudioWelcome={suppressFeatureStudioWelcome}
                            newProductDraftRef={newProductDraftRef}
                            newProductFormRef={newProductFormRef}
                        onWidthChange={handleChatWidthChange}
                        onTabChange={() => {
                            // Feature Studio now has one SDD workspace. Its stage
                            // stepper owns navigation instead of the retired SDLC tabs.
                            setFeatureStudioTab("sdd");
                        }}
                        onBackToProjects={projectHandlers.handleBackToProjects}
                        onViewChange={handleViewChange}
                        onAgentEvent={(name, payload) => {
                            if (name === "integration_connection_changed") {
                                // The Integrations page is the subscriber. If it is
                                // not mounted, intentionally do nothing; its normal
                                // mount fetch will load current server state later.
                                if (currentView === "integrations") {
                                    setIntegrationsRefreshSignal(prev => prev + 1);
                                }
                                return;
                            }

                            if (name === "uploaded_file_assignment_changed") {
                                setUploadsRefreshSignal(prev => prev + 1);
                                return;
                            }

                            if (name === "generate_work_product") {
                                handleGenerateWorkProductRequest(payload as AppChatEvents["generate_work_product"]);
                                return;
                            }

                            if (name === "open_add_feature") {
                                const featurePayload = payload as AppChatEvents["open_add_feature"];
                                const targetProject = initiatives.find(
                                    (initiative: any) =>
                                        String(initiative.project_id ?? initiative.id) === String(featurePayload.projectId)
                                );
                                if (!targetProject) {
                                    toast.error(`Product "${featurePayload.projectName}" is not available in the current workspace.`);
                                    return;
                                }

                                handleAgentProjectSelect(targetProject, () => {
                                    setShowAddFeatureModal(true);
                                });
                                return;
                            }

                            if (name === "open_add_user_story") {
                                const storyPayload = payload as AppChatEvents["open_add_user_story"];
                                const targetProject = initiatives.find(
                                    (initiative: any) =>
                                        String(initiative.project_id ?? initiative.id) === String(storyPayload.projectId)
                                );
                                if (!targetProject) {
                                    toast.error(`Product "${storyPayload.projectName}" is not available in the current workspace.`);
                                    return;
                                }

                                const targetFeature = (targetProject.features || []).find(
                                    (feature: any) =>
                                        String(feature.featureid ?? feature.id) === String(storyPayload.featureId)
                                );
                                handleAgentProjectSelect(targetProject, () => {
                                    setSelectedFeature(
                                        (targetFeature as any) ?? {
                                            id: String(storyPayload.featureId),
                                            featureid: storyPayload.featureId,
                                            name: storyPayload.featureName,
                                            title: storyPayload.featureName,
                                            projectid: storyPayload.projectId
                                        }
                                    );
                                    setSelectedUserStory(null);
                                    setHierarchyExpandedItems(previous =>
                                        Array.from(new Set([...previous, `P:${storyPayload.projectId}`, `F:${storyPayload.featureId}`]))
                                    );
                                    setShowAddUserStoryModal(true);
                                });
                                return;
                            }

                            if (name === "refresh_projects") {
                                handleRefreshProjects();
                                setProductsRefreshSignal(prev => prev + 1);
                                return;
                            }

                            if (name === "refresh_product_context") {
                                const contextPayload = payload as AppChatEvents["refresh_product_context"];
                                const currentProjectId =
                                    (currentProjectFromStore as any)?.project_id ||
                                    (selectedProject as any)?.project_id ||
                                    (selectedFeature as any)?.projectid;
                                if (
                                    contextPayload?.projectId &&
                                    currentProjectId &&
                                    Number(contextPayload.projectId) === Number(currentProjectId)
                                ) {
                                    dispatch(fetchProjectRebuildHierarchy(Number(contextPayload.projectId)));
                                    refreshWorkProductsForCurrentContext();
                                }
                                return;
                            }

                            if (name === "navigate_to_previous_screen") {
                                if (currentView === "editor") {
                                    handleViewChange("features");
                                    return;
                                }
                                if (currentView === "features") {
                                    projectHandlers.handleBackToProjects();
                                    return;
                                }
                                if (currentView !== "projects") {
                                    handleViewChange("projects");
                                }
                                return;
                            }

                            if (name === "navigate_to_feature_studio") {
                                // Mirror the editor's top-left back button behavior.
                                handleViewChange("features");
                                return;
                            }

                            if (name === "navigate_to_sdd_product_hierarchy" || name === "navigate_to_sdd_scope_picker" || name === "navigate_to_sdd_progress_report") {
                                handleViewChange("features");
                                // Defer until FeatureStudioView is mounted when the command
                                // originates from another FDE screen (for example, an editor).
                                window.setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent("fde:sdd-navigation", { detail: { name } }));
                                }, 0);
                                return;
                            }

                            if (name === "navigate_to_sdd_stage") {
                                handleViewChange("features");
                                window.setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent("fde:sdd-navigation", { detail: { name, ...payload } }));
                                }, 0);
                                return;
                            }

                            if (name === "navigate_to_new_product") {
                                const newProductPayload = payload as AppChatEvents["navigate_to_new_product"];
                                setNewProductDraftId(null);
                                setNewProductInitialKind(newProductPayload.product_mode ?? null);
                                setNewProductInitialPatch(null);
                                handleViewChange("new-product");
                                return;
                            }

                            if (name === "navigate_to_new_context_mesh") {
                                setStandaloneContextMeshDraftId(null);
                                handleViewChange("new-context-mesh", { skipNewProductGuard: true });
                                return;
                            }

                            if (name === "navigate_to_context_mesh") {
                                // The event can arrive after access changes while the chat turn is
                                // in flight. Fail closed before mutating mesh state or browser history.
                                if (!canOpenStandaloneContextMesh(manifest)) return;
                                const contextMeshPayload = payload as AppChatEvents["navigate_to_context_mesh"];
                                setStandaloneContextMeshId(contextMeshPayload.contextMeshId);
                                setStandaloneContextMeshSection("console");
                                setNewlyCreatedContextMeshId(null);
                                handleViewChange("context-mesh-console", { skipNewProductGuard: true });
                                if (typeof window !== "undefined") {
                                    window.history.pushState(
                                        { view: "context-mesh-console", contextMeshId: contextMeshPayload.contextMeshId },
                                        "",
                                        contextMeshHubUrl(contextMeshPayload.contextMeshId)
                                    );
                                }
                                return;
                            }

                            if (name === "navigate_to_context_mesh_hub") {
                                setStandaloneContextMeshId(null);
                                setStandaloneContextMeshSection("console");
                                setNewlyCreatedContextMeshId(null);
                                handleViewChange("context-mesh-console", { skipNewProductGuard: true });
                                return;
                            }

                            if (name === "navigate_to_integrations") {
                                handleViewChange("integrations");
                                return;
                            }

                            if (name === "navigate_to_files") {
                                handleViewChange("uploads");
                                return;
                            }

                            if (name === "navigate_to_project") {
                                const projectPayload = payload as AppChatEvents["navigate_to_project"];
                                // The tool emits a canonical resolved id. Prefer
                                // that exact identity and use the name only as a
                                // compatibility fallback when the id is absent
                                // from the currently loaded store.
                                const targetById = initiatives.find(
                                    (init: any) =>
                                        String(init.project_id) === String(projectPayload.projectId) ||
                                        String(init.id) === String(projectPayload.projectId)
                                );
                                const target = targetById ?? initiatives.find(
                                    (init: any) =>
                                        String(init.project_name ?? init.name ?? "").trim().toLowerCase() ===
                                            String(projectPayload.projectName ?? "").trim().toLowerCase()
                                );
                                if (target) {
                                    handleAgentProjectSelect(target);
                                } else {
                                    console.warn(
                                        "[AIChatPanel] navigate_to_project: project not found in store",
                                        projectPayload
                                    );
                                }
                                return;
                            }

                            if (name === "navigate_to_feature") {
                                const featurePayload = payload as AppChatEvents["navigate_to_feature"];
                                const targetProject = initiatives.find(
                                    (init: any) =>
                                        String(init.project_id) === String(featurePayload.projectId) ||
                                        String(init.id) === String(featurePayload.projectId)
                                );

                                if (!targetProject) {
                                    console.warn("[AIChatPanel] navigate_to_feature: project not found in store", featurePayload);
                                    return;
                                }

                                const targetFeature = (targetProject.features || []).find(
                                    (feature: any) =>
                                        String(feature.featureid ?? feature.id) === String(featurePayload.featureId)
                                );

                                if (!targetFeature) {
                                    console.warn(
                                        "[AIChatPanel] navigate_to_feature: feature not found in project, using payload fallback",
                                        featurePayload
                                    );
                                }

                                handleAgentProjectSelect(targetProject, () => {
                                    setSelectedFeature(
                                        (targetFeature as any) ?? {
                                            id: String(featurePayload.featureId),
                                            featureid: featurePayload.featureId,
                                            name: featurePayload.featureName,
                                            title: featurePayload.featureName,
                                            projectid: featurePayload.projectId
                                        }
                                    );
                                    setSelectedUserStory(null);
                                    setHierarchyExpandedItems((prev) =>
                                        Array.from(new Set([...prev, `P:${featurePayload.projectId}`, `F:${featurePayload.featureId}`]))
                                    );
                                    window.dispatchEvent(new CustomEvent("fde:sdd-scope-selection", { detail: { kind: "feature" } }));
                                });
                                return;
                            }

                            if (name === "navigate_to_user_story") {
                                const storyPayload = payload as AppChatEvents["navigate_to_user_story"];
                                const targetProject = initiatives.find(
                                    (init: any) =>
                                        String(init.project_id) === String(storyPayload.projectId) ||
                                        String(init.id) === String(storyPayload.projectId)
                                );

                                if (!targetProject) {
                                    console.warn("[AIChatPanel] navigate_to_user_story: project not found in store", storyPayload);
                                    return;
                                }

                                const targetFeature = (targetProject.features || []).find(
                                    (feature: any) =>
                                        String(feature.featureid ?? feature.id) === String(storyPayload.featureId)
                                );

                                if (!targetFeature) {
                                    console.warn(
                                        "[AIChatPanel] navigate_to_user_story: feature not found in project, using payload fallback",
                                        storyPayload
                                    );
                                }

                                handleAgentProjectSelect(targetProject, () => {
                                    setSelectedFeature(
                                        (targetFeature as any) ?? {
                                            id: String(storyPayload.featureId),
                                            featureid: storyPayload.featureId,
                                            name: storyPayload.featureName,
                                            title: storyPayload.featureName,
                                            projectid: storyPayload.projectId
                                        }
                                    );
                                    setSelectedUserStory({
                                        id: storyPayload.userStoryId,
                                        story_number: storyPayload.storyNumber,
                                        title: storyPayload.storyTitle,
                                        feature_id: storyPayload.featureId
                                    });
                                    setHierarchyExpandedItems((prev) =>
                                        Array.from(new Set([...prev, `P:${storyPayload.projectId}`, `F:${storyPayload.featureId}`]))
                                    );
                                    window.dispatchEvent(new CustomEvent("fde:sdd-scope-selection", { detail: { kind: "story" } }));
                                });
                            }
                        }}
                    />
                </MuiBox>
            </MuiBox>

            {/* Modals and Panels */}
            <InitiativeCreationModal
                open={showAIModal}
                onClose={() => setShowAIModal(false)}
                messages={chatMessages}
                onMessagesChange={setChatMessages}
                chatInput={currentChatInput}
                onChatInputChange={setCurrentChatInput}
                chatStep={chatStep}
                onSendMessage={chatHandlers.handleSendChatMessage}
                onKeyPress={chatHandlers.handleChatKeyPress}
                isLoadingNewInitiative={isLoadingNewInitiative}
                loadingProgress={loadingProgress}
                initiativeData={initiativeData}
                onFileInputRef={fileInputRef}
                onCreateInitiative={chatHandlers.handleCreateInitiative}
            />

            {/* UpdateProductModal is Add-Feature-only now — product refresh moved to NewProductScreen mode="refresh" (KAN-1464); delete when Add-Feature gets its own flow */}
            <UpdateProductModal
                open={showAddFeatureModal}
                onClose={() => setShowAddFeatureModal(false)}
                productId={currentProjectFromStore?.project_id || selectedProject?.["project_id"] || selectedProject?.["projectid"] || 0}
                initialData={{
                    productId: currentProjectFromStore?.project_id || selectedProject?.["project_id"] || selectedProject?.["projectid"] || 0,
                    type: "NewFeature",
                    name: "",
                    description: "",
                    users: "",
                    problem: "",
                    solution: "",
                    files: [],
                    jiraProject: "",
                    githubProject: ""
                }}
                onUpdate={async (data) => {
                    // Refresh features list after successful creation/update
                    console.log("Feature created/updated successfully, refreshing features list and work products");
                    setFeaturesRefreshTrigger(prev => prev + 1);

                    // Refresh work products at all levels to show orange dots for needs_rebuild
                    const effectiveProjectId = currentProjectFromStore?.project_id || selectedProject?.project_id || selectedProject?.["projectid"];
                    if (effectiveProjectId) {
                        console.log("🔄 Refreshing work products at PRODUCT level after feature creation");
                        dispatch(fetchWorkProductsByPhase({ projectId: Number(effectiveProjectId), level: "PRODUCT" }));
                    }

                    setShowAddFeatureModal(false);
                }}
            />

            <AddNewUserStoryModal
                open={showAddUserStoryModal}
                onClose={() => setShowAddUserStoryModal(false)}
                onUserStoryCreated={() => {
                    // Refresh user stories list after successful creation
                    console.log("User story created successfully, refreshing user stories list and work products");

                    // Refresh the feature's user stories
                    const featureId = selectedFeature?.id || selectedFeature?.featureid;
                    if (featureId && token) {
                        dispatch(fetchFeatureUserStories({ featureId, idToken: token }));
                    }

                    // Refresh work products at all levels to show orange dots for needs_rebuild
                    const effectiveProjectId = currentProjectFromStore?.project_id || selectedProject?.project_id || (selectedFeature as any)?.projectid;
                    if (effectiveProjectId) {
                        console.log("🔄 Refreshing work products at PRODUCT level after user story creation");
                        dispatch(fetchWorkProductsByPhase({ projectId: Number(effectiveProjectId), level: "PRODUCT" }));
                    }

                    // Also trigger features refresh in case we need to update other data
                    setFeaturesRefreshTrigger(prev => prev + 1);
                }}
                projectId={currentProjectFromStore?.project_id || selectedProject?.project_id || (selectedFeature as any)?.projectid}
                featureId={selectedFeature?.id || selectedFeature?.featureid}
                featureName={selectedFeature?.title || selectedFeature?.name}
            />

            {/* Edit Warning Modal */}
            <MuiDialog open={showEditWarning} onClose={() => setShowEditWarning(false)} maxWidth="sm" fullWidth>
                <MuiDialogTitle>
                    <MuiBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <WarningIcon sx={{ color: "warning.main" }} />
                        Confirm Feature Edit
                    </MuiBox>
                </MuiDialogTitle>
                <MuiDialogContent>
                    <MuiTypography>
                        Editing this feature may affect Work Products across the Software Development Life Cycle (SDLC). Do you want to continue?
                    </MuiTypography>
                </MuiDialogContent>
                <MuiDialogActions>
                    <MuiButton onClick={() => setShowEditWarning(false)}>Cancel</MuiButton>
                    <MuiButton onClick={handleEditWarningConfirm} variant="contained" color="warning">
                        Continue Editing
                    </MuiButton>
                </MuiDialogActions>
            </MuiDialog>

            {/* Work Products Impact Modal */}
            <MuiDialog open={showWorkProductsImpactModal} onClose={() => setShowWorkProductsImpactModal(false)} maxWidth="md" fullWidth>
                <MuiDialogTitle>Confirm Changes to Work Products</MuiDialogTitle>
                <MuiDialogContent>
                    <MuiTypography sx={{ mb: 2 }}>The following Work Products will be affected by your changes:</MuiTypography>
                    <MuiBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {impactedWorkProducts.map((workProduct, index) => (
                            <MuiBox key={index} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, bgcolor: "background.default", borderRadius: 1 }}>
                                <CheckCircleIcon sx={{ color: "primary.main", fontSize: 20 }} />
                                <MuiTypography variant="body2">{workProduct}</MuiTypography>
                            </MuiBox>
                        ))}
                    </MuiBox>
                    <MuiTypography sx={{ mt: 2, fontSize: "0.875rem", color: "text.secondary" }}>
                        All affected Work Products are selected by default. Do you wish to proceed with these changes?
                    </MuiTypography>
                </MuiDialogContent>
                <MuiDialogActions>
                    <MuiButton onClick={() => setShowWorkProductsImpactModal(false)}>Cancel</MuiButton>
                    <MuiButton onClick={handleWorkProductsImpactConfirm} variant="contained">
                        Proceed with Changes
                    </MuiButton>
                </MuiDialogActions>
            </MuiDialog>

            {/* Delete Feature Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Feature</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{featureToDelete?.name || featureToDelete?.title}"? All user stories under this feature will also
                            be deleted. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={() => {
                                setShowDeleteConfirmDialog(false);
                                setFeatureToDelete(null);
                            }}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDeleteFeature} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete User Story Confirmation Dialog */}
            <AlertDialog open={showDeleteUserStoryDialog} onOpenChange={setShowDeleteUserStoryDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete User Story</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{userStoryToDelete?.title}"? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={() => {
                                setShowDeleteUserStoryDialog(false);
                                setUserStoryToDelete(null);
                            }}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDeleteUserStory} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Jira Sync Feedback */}
            <MuiSnackbar
                open={jiraSyncFeedback.show}
                autoHideDuration={6000}
                onClose={() => setJiraSyncFeedback(prev => ({ ...prev, show: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <MuiAlert
                    onClose={() => setJiraSyncFeedback(prev => ({ ...prev, show: false }))}
                    severity={jiraSyncFeedback.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {jiraSyncFeedback.message}
                </MuiAlert>
            </MuiSnackbar>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.ppt,.pptx,.xls,.xlsx"
                onChange={handleFileInputChange}
            />
        </ThemeProvider>
    );
}

export default function App() {
    return (
        <BatchGenerationProvider>
            <BackgroundActivityProvider>
                <AppContent />
            </BackgroundActivityProvider>
        </BatchGenerationProvider>
    );
}
